/* eslint-disable react/prop-types, react/no-array-index-key, no-nested-ternary */
import PropTypes from "prop-types";
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Server, Cloud, ShieldCheck, Activity, Lock, Sparkles, Plus, CheckCircle2, Mic, Linkedin, MessageCircle, Mail, TrendingUp, Trophy, ArrowDown, MessageSquare, Send, AlertTriangle, Pencil } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { TeamSettingsSection } from "@/pages/settings/TeamSettingsSection";
import { useTeamStore, useAssistantName, teamActivity } from "@/store/useTeamStore";
import { useAppStore } from "@/store/useAppStore";
import { teamApi } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChartContainer } from "@/components/common/ChartContainer";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, RadialBarChart, RadialBar } from "recharts";
import { revenueAttribution } from "@/mocks/phase5";
const pairedAccounts = [];
const aiVsHumanCards = { deflected: 0, sdrTimeSaved: "0h", revenueAttributed: "$0", hoursReplaced: 0 };
const campaignAnalytics = [];
const funnelData = Array.from({ length: 8 }, () => ({ count: 0 }));
import { useSafetyStore } from "@/store/useSafetyStore";
import { TimezoneSelect } from "@/components/common/TimezoneSelect";
import { useConnectionsStore } from "@/store/useConnectionsStore";
import { usePersonaStore, SAMPLE_BANK, bucketForTones } from "@/store/usePersonaStore";
import { sampleFieldFilled, voiceFingerprintSampleCount } from "@/lib/personaSamples";
import { userFirstName } from "@/lib/userDisplay";
import { PersonaAvatarButton } from "@/components/persona/PersonaAvatarButton";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useConnectionsSettings } from "@/pages/settings/useConnectionsSettings";
import { ConnectionsSettingsView } from "@/pages/settings/ConnectionsSettingsView";
import { ChangePasswordSection } from "@/pages/settings/ChangePasswordSection";
import { LeadNotificationTemplateModal } from "@/pages/settings/LeadNotificationTemplateModal";
import { SETTINGS_EXTENDED_FEATURES_ENABLED } from "@/lib/featureFlags";

const SETTINGS_TABS = [
  { label: "Team", path: "/settings/team" },
  { label: "AI Agents", path: "/settings/agents" },
  { label: "Persona", path: "/settings/persona" },
  { label: "Connections", path: "/settings/connections" },
  { label: "General", path: "/settings/general" },
  { label: "Routing", path: "/settings/routing", extendedOnly: true },
  { label: "Safety", path: "/settings/safety", extendedOnly: true },
  { label: "Instance", path: "/settings/instance", extendedOnly: true },
  { label: "Performance", path: "/settings/performance", extendedOnly: true },
  { label: "Billing", path: "/settings/billing", extendedOnly: true },
  { label: "Account", path: "/settings/account" },
  { label: "Referrals", path: "/settings/referrals", extendedOnly: true },
  { label: "Privacy", path: "/settings/privacy", extendedOnly: true },
  { label: "Audit", path: "/settings/audit", extendedOnly: true },
];

function SettingsNav() {
  const tabs = SETTINGS_TABS.filter((t) => {
    if (!SETTINGS_EXTENDED_FEATURES_ENABLED && t.extendedOnly) return false;
    return true;
  });

  return (
    <div className="flex items-center gap-0 px-3 sm:px-6 border-b border-slate-200 bg-white overflow-x-auto scrollbar-thin shrink-0">
      {tabs.map((t) => (
        <Link key={t.path} to={t.path} data-testid={`settings-tab-${t.label.toLowerCase()}`} className={cn("inline-flex items-center px-3 min-h-10 sm:h-9 text-[12.5px] font-medium border-b-2 -mb-px whitespace-nowrap transition-colors", window.location.pathname === t.path ? "text-indigo-700 border-indigo-500" : "text-slate-500 border-transparent hover:text-slate-800")}>{t.label}</Link>
      ))}
    </div>
  );
}

function Avatar({ member, size = 28 }) {
  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      className={cn("rounded-full bg-gradient-to-br text-white font-semibold flex items-center justify-center shrink-0", member.color)}
    >
      {member.first?.[0]}{member.name.split(" ")[1]?.[0]}
    </div>
  );
}

Avatar.propTypes = {
  member: PropTypes.shape({
    color: PropTypes.string,
    first: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  size: PropTypes.number,
};

function Shell({ title, desc, children }) {
  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 overflow-hidden">
      <PageHeader title={title} description={desc} />
      <SettingsNav />
      <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin">{children}</div>
    </div>
  );
}

export { Shell };

Shell.propTypes = {
  title: PropTypes.string,
  desc: PropTypes.string,
  children: PropTypes.node,
};

// V2 unlock — MFA TOTP setup section. Lives under /settings/team as a "Security" block
// so we don't add another top-level settings tab. All persistence is in useAppStore.mfa;
// no real TOTP secret is generated — any 6-digit code is mock-accepted.
function SecuritySection() {
  const { mfa, setMfa, user } = useAppStore();
  const enabled = !!mfa?.enabled;
  const verified = !!mfa?.verified_at;
  const [step, setStep] = useState(enabled ? "done" : "off"); // off | setup | verify | done
  const [code, setCode] = useState("");
  const [revealCodes, setRevealCodes] = useState(false);

  // Build a fake TOTP otpauth URI for the QR (mock — no real secret derivation)
  const secret = "JBSWY3DPEHPK3PXPSELLIXMFA2026";
  const otpauth = `otpauth://totp/Sellyx:${encodeURIComponent(user?.email || "founder@sellix.ai")}?secret=${secret}&issuer=Sellyx&algorithm=SHA1&digits=6&period=30`;

  const onEnable = () => { setStep("setup"); setCode(""); };
  const onVerify = () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code from your authenticator app"); return; }
    const backupCodes = Array.from({ length: 8 }, () => Math.random().toString(36).slice(2, 6).toUpperCase() + "-" + Math.random().toString(36).slice(2, 6).toUpperCase());
    setMfa({ enabled: true, verified_at: new Date().toISOString(), backup_codes: backupCodes });
    setRevealCodes(true);
    setStep("done");
    toast.success("Two-factor authentication enabled");
  };
  const onDisable = () => {
    setMfa({ enabled: false, verified_at: null, backup_codes: [] });
    setStep("off"); setCode(""); setRevealCodes(false);
    toast.success("Two-factor authentication disabled");
  };
  const onRegen = () => {
    const codes = Array.from({ length: 8 }, () => Math.random().toString(36).slice(2, 6).toUpperCase() + "-" + Math.random().toString(36).slice(2, 6).toUpperCase());
    setMfa({ ...mfa, backup_codes: codes });
    setRevealCodes(true);
    toast.success("Regenerated 8 new backup codes");
  };
  const copyAll = async () => {
    try { await navigator.clipboard.writeText((mfa?.backup_codes || []).join("\n")); toast.success("Backup codes copied"); }
    catch { toast.error("Couldn't copy — select manually"); }
  };

  return (
    <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="security-section">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 flex items-center justify-center shrink-0"><ShieldCheck size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold tracking-tight text-slate-900">Security</div>
            <div className="text-[12px] text-slate-500 mt-0.5">Protect your founder account with two-factor authentication (TOTP).</div>
          </div>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
          <span className="text-[12px] text-slate-700">Two-factor authentication</span>
          <Switch checked={enabled} onCheckedChange={(v) => { if (v) onEnable(); else onDisable(); }} data-testid="mfa-toggle" />
        </div>
      </div>

      {step === "setup" && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="mfa-setup">
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
            <div className="text-[12px] font-semibold text-slate-900">Step 1 · Scan the QR</div>
            <div className="text-[11.5px] text-slate-500 mt-0.5 mb-3">Use Google Authenticator, 1Password, or Authy.</div>
            <div className="rounded-lg bg-white ring-1 ring-slate-200 p-3 inline-block">
              <MFAQRCode value={otpauth} />
            </div>
            <div className="mt-2 text-[10.5px] text-slate-500 break-all">Or paste this secret: <span className="font-mono text-slate-700">{secret}</span></div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
            <div className="text-[12px] font-semibold text-slate-900">Step 2 · Enter the 6-digit code</div>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123 456" inputMode="numeric" maxLength={6} className="mt-2 h-11 sm:h-10 font-mono text-[16px] tracking-[0.3em] text-center" data-testid="mfa-code-input" />
            <div className="mt-3 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2">
              <Button size="sm" variant="outline" className="h-10 sm:h-8 w-full sm:w-auto" onClick={() => { setStep("off"); setMfa({ enabled: false, verified_at: null, backup_codes: [] }); }}>Cancel</Button>
              <Button size="sm" className="h-10 sm:h-8 w-full sm:w-auto" onClick={onVerify} data-testid="mfa-verify">Verify & enable</Button>
            </div>
            <div className="mt-3 text-[10.5px] text-slate-500">Tip: any 6-digit code is accepted in this V1 demo.</div>
          </div>
        </div>
      )}

      {step === "done" && enabled && (
        <div className="mt-5" data-testid="mfa-enabled">
          <div className="flex items-center gap-2 text-[12px] text-emerald-700"><ShieldCheck size={13} /> Two-factor authentication is ON{verified && <span className="text-slate-500"> · verified {new Date(mfa.verified_at).toLocaleDateString()}</span>}</div>
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/40 p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[12px] font-semibold text-slate-900">Backup codes</div>
                <div className="text-[11px] text-slate-500">Each code can be used once if you lose your authenticator.</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="h-9 sm:h-8" onClick={() => setRevealCodes((v) => !v)} data-testid="mfa-reveal">{revealCodes ? "Hide" : "Reveal"}</Button>
                <Button size="sm" variant="outline" className="h-9 sm:h-8" onClick={copyAll} data-testid="mfa-copy">Copy all</Button>
                <Button size="sm" variant="outline" className="h-9 sm:h-8" onClick={onRegen} data-testid="mfa-regen">Regenerate</Button>
              </div>
            </div>
            {revealCodes && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="mfa-backup-codes">
                {(mfa.backup_codes || []).map((c) => (
                  <code key={c} className="text-[12px] font-mono text-slate-800 bg-white ring-1 ring-slate-200 rounded px-2 py-1.5 select-all">{c}</code>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {step === "off" && !enabled && (
        <div className="mt-4 text-[12px] text-slate-500">Two-factor authentication is currently <span className="font-semibold text-slate-700">disabled</span>. Toggle it on to protect against credential theft.</div>
      )}
    </section>
  );
}

// Tiny wrapper so qrcode.react is lazy-loaded once and gracefully degrades if missing.
import { QRCodeSVG } from "qrcode.react";

function MFAQRCode({ value }) {
  return <QRCodeSVG value={value} size={128} bgColor="#ffffff" fgColor="#0f172a" level="M" includeMargin={false} data-testid="mfa-qr" />;
}

MFAQRCode.propTypes = {
  value: PropTypes.string.isRequired,
};

export function SettingsTeam() {
  return (
    <TeamSettingsSection
      Shell={Shell}
      showRoleGuide={SETTINGS_EXTENDED_FEATURES_ENABLED}
      after={SETTINGS_EXTENDED_FEATURES_ENABLED ? (
        <div className="px-4 sm:px-6 pb-6"><SecuritySection /></div>
      ) : null}
    />
  );
}

export function SettingsInstance() {
  const { instanceType, setInstanceType } = useTeamStore();
  const cards = [
    { id: "private-saas", title: "Private SaaS", price: "$999/mo", subtitle: "Sellyx-managed single-tenant container", Icon: Cloud, accent: "from-indigo-500 to-violet-600", includes: ["Dedicated tenant", "Encrypted at rest", "99.95% SLA", "Up to 5 team members"] },
    { id: "self-hosted", title: "Self-hosted Cloud", price: "$1,499/mo", subtitle: "Your AWS / GCP — BYOK", Icon: Server, accent: "from-emerald-500 to-teal-600", includes: ["Your VPC", "Bring your own keys", "Your audit logs", "Customer-managed updates"] },
  ];
  const current = cards.find((c) => c.id === instanceType) || cards[0];

  return (
    <Shell title="Instance" desc="Where Maya lives and how she runs.">
      <div className="p-6 max-w-6xl space-y-6">

        {/* Current instance hero */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-50/40 via-white to-white p-5" data-testid="instance-hero">
          <div className="flex items-start gap-5 flex-wrap">
            <div className={cn("h-12 w-12 rounded-xl text-white flex items-center justify-center shadow-sm bg-gradient-to-br", current.accent)}>
              <current.Icon size={20} strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold">Your instance</span>
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>
              </div>
              <div className="mt-1 text-[24px] font-semibold tracking-tight text-slate-900">{current.title}</div>
              <div className="text-[12px] text-slate-500">{current.subtitle} · us-west-2 · Sellyx-managed</div>
            </div>
            <div className="text-right">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Uptime · 30d</div>
              <div className="text-[26px] font-semibold tabular-nums text-emerald-600 leading-none mt-1">99.97%</div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Data location", value: "US-West (Oregon)" },
              { label: "Encryption", value: "AES-256 at rest · TLS 1.3 in transit" },
              { label: "Compliance", value: "SOC 2 Type II · GDPR" },
              { label: "BYO KMS", value: "Supported" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg bg-white ring-1 ring-slate-200 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{s.label}</div>
                <div className="text-[12px] text-slate-800 font-medium mt-0.5 leading-tight">{s.value}</div>
              </div>
            ))}
          </div>
          {/* CPU / Memory gauges */}
          <div className="mt-3 grid grid-cols-2 gap-3">
            {[
              { label: "CPU utilization", value: 32, tint: "from-indigo-400 to-violet-500" },
              { label: "Memory utilization", value: 41, tint: "from-emerald-400 to-teal-500" },
            ].map((g) => (
              <div key={g.label} className="rounded-lg bg-white ring-1 ring-slate-200 px-3 py-2">
                <div className="flex items-center justify-between text-[10.5px]">
                  <span className="uppercase tracking-wider text-slate-400 font-semibold">{g.label}</span>
                  <span className="tabular-nums text-slate-700 font-semibold">{g.value}%</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={cn("h-full bg-gradient-to-r", g.tint)} style={{ width: `${g.value}%` }} />
                </div>
              </div>
            ))}
          </div>

          {/* Maya's runtime */}
          <div className="mt-4 rounded-lg bg-slate-900 text-slate-100 px-4 py-3">
            <div className="text-[10.5px] uppercase tracking-wider text-indigo-300 font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> Maya&apos;s runtime</div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11.5px] font-mono">
              <div><span className="text-slate-400">runtime</span><br /><span className="text-emerald-300">openclaw v2.4.1</span></div>
              <div><span className="text-slate-400">model</span><br /><span className="text-emerald-300">gpt-5.5-turbo</span></div>
              <div><span className="text-slate-400">vector store</span><br /><span className="text-emerald-300">pinecone-east-1</span></div>
              <div><span className="text-slate-400">active sessions</span><br /><span className="text-emerald-300">14 live · 89 today</span></div>
            </div>
          </div>
        </div>

        {/* Instance type comparison */}
        <section>
          <div className="text-[15px] font-semibold text-slate-900">Instance types</div>
          <p className="text-[12px] text-slate-500 mt-0.5 mb-3">Same Maya. Different runtime locations. Pick what fits your trust + data needs.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {cards.map((c) => {
              const active = c.id === instanceType;
              return (
                <div key={c.id} className={cn("rounded-2xl border bg-white p-5 transition-all", active ? "border-indigo-400 ring-2 ring-indigo-200 shadow-sm" : "border-slate-200 hover:border-slate-300")} data-testid={`instance-${c.id}`}>
                  <div className={cn("h-9 w-9 rounded-lg text-white flex items-center justify-center bg-gradient-to-br", c.accent)}>
                    <c.Icon size={16} />
                  </div>
                  <div className="mt-3 text-[15px] font-semibold text-slate-900">{c.title}</div>
                  <div className="text-[11.5px] text-slate-500">{c.subtitle}</div>
                  <div className="mt-1.5 text-[13.5px] text-indigo-700 font-semibold tabular-nums">{c.price}</div>
                  {active && <Badge className="mt-2 bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>}
                  <ul className="mt-3 space-y-1 text-[11.5px] text-slate-700">
                    {c.includes.map((x) => <li key={x} className="inline-flex items-center gap-1.5"><CheckCircle2 size={10} className="text-emerald-600" /> {x}</li>)}
                  </ul>
                  {c.id === "hardware" && (
                    <div className="mt-3 rounded-lg ring-1 ring-slate-200 bg-slate-50/60 p-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-12 rounded-md bg-white ring-1 ring-slate-700 flex items-center justify-center relative overflow-hidden px-0.5">
                          <img src="/sellyx-mark.png" alt="" className="h-4 w-4 object-contain" />
                          <span className="absolute top-1 right-1 h-1 w-1 rounded-full bg-emerald-400 animate-pulse" />
                        </div>
                        <div className="text-[10px] text-slate-600 leading-tight">
                          <div className="font-mono text-slate-800">SLX-2641</div>
                          <div>Firmware 1.4.2 · LED green</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {c.id === "self-hosted" && (
                    <div className="mt-3 rounded-lg ring-1 ring-slate-200 bg-slate-50/60 p-2.5 text-[10.5px] text-slate-600 font-mono inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Connected to AWS · aman-aws-prod
                    </div>
                  )}
                  <Button
                    variant={active ? "outline" : "default"}
                    size="sm" className="w-full mt-4 h-8 text-[11.5px]"
                    onClick={() => active ? null : toast("Contact your Sellyx rep to migrate — typical migration takes 24h with zero downtime.")}
                    data-testid={`instance-switch-${c.id}`}
                  >
                    {active ? "Current" : "Switch to this"}
                  </Button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Hardware appliance preview (when hardware is active or shown) */}
        {instanceType === "hardware" && (
          <section>
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-slate-100 p-6">
              <div className="flex items-center gap-5 flex-wrap">
                <div className="relative">
                  <div className="h-24 w-36 rounded-xl bg-white ring-1 ring-slate-700 flex items-center justify-center shadow-lg relative overflow-hidden">
                    <img src="/sellyx-logo.png" alt="Sellyx" className="max-h-16 max-w-[8.5rem] object-contain" />
                    <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="absolute bottom-2 left-3 text-[8px] font-mono text-slate-500">LAN 1G</span>
                  </div>
                </div>
                <div className="flex-1 min-w-[260px]">
                  <div className="text-[10.5px] uppercase tracking-wider text-indigo-300 font-semibold">About Sellyx Box</div>
                  <div className="mt-1 text-[18px] font-semibold tracking-tight">Maya runs entirely on-premise.</div>
                  <p className="mt-1 text-[12.5px] text-slate-300 max-w-xl leading-relaxed">Pair WhatsApp via a Bluetooth-linked phone. Your conversations never leave your office. Lifetime firmware updates pushed over your own network.</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Data sovereignty footer */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { label: "SOC 2 Type II", Icon: ShieldCheck },
                { label: "GDPR", Icon: Lock },
                { label: "AES-256", Icon: Server },
              ].map((p) => (
                <span key={p.label} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700">
                  <p.Icon size={10} /> {p.label}
                </span>
              ))}
            </div>
            <div className="text-[11.5px] text-slate-600 ml-auto max-w-md leading-snug">
              <span className="font-semibold text-slate-800">Your data lives only in this instance.</span> Sellyx has zero remote access without your invitation.
            </div>
          </div>
        </section>

        {/* Dev-only quick instance picker for demo */}
        <div className="text-[10px] text-slate-400 inline-flex items-center gap-1.5">
          <span>Switch demo instance:</span>
          {cards.map((c) => (
            <button key={c.id} onClick={() => setInstanceType(c.id)} className={cn("font-mono px-1.5 py-0.5 rounded ring-1", c.id === instanceType ? "bg-indigo-50 text-indigo-700 ring-indigo-200" : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50")} data-testid={`dev-instance-${c.id}`}>{c.id}</button>
          ))}
        </div>
      </div>
    </Shell>
  );
}

function billingTierPrice(tier) {
  if (tier.amount_cents === 0) return "Free";
  if (tier.amount_cents == null) return "Custom";
  return `$${(tier.amount_cents / 100).toFixed(0)}/mo`;
}

function BillingTierCta({ tier, isCurrent, upgrading, onUpgrade }) {
  if (isCurrent) {
    return (
      <Badge className="mt-3 bg-emerald-100 text-emerald-700 text-[10px] self-start" data-testid={`billing-tier-${tier.id}-current-badge`}>Active</Badge>
    );
  }
  if (tier.id === "trial") return null;
  if (tier.id === "enterprise") {
    return (
      <Button size="sm" variant="outline" className="h-7 mt-3 text-[10.5px]" onClick={() => toast.message("Contact sales — we'll be in touch")} data-testid={`billing-tier-${tier.id}-contact`}>Contact sales</Button>
    );
  }
  return (
    <Button size="sm" className="h-7 mt-3 text-[10.5px]" onClick={() => onUpgrade(tier.id)} disabled={upgrading === tier.id} data-testid={`billing-tier-${tier.id}-upgrade`}>
      {upgrading === tier.id ? "Redirecting…" : `Upgrade to ${tier.label}`}
    </Button>
  );
}

BillingTierCta.propTypes = {
  tier: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  isCurrent: PropTypes.bool,
  upgrading: PropTypes.string,
  onUpgrade: PropTypes.func.isRequired,
};

export function SettingsBilling() {
  const navigate = useNavigate();
  const { refreshAuth } = useAppStore();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const { billingApi } = await import("@/lib/api");
      const p = await billingApi.plan();
      setPlan(p);
      await refreshAuth?.();
    } catch (e) {
      toast.error(e?.message || "Failed to load plan");
    } finally {
      setLoading(false);
    }
  }, [refreshAuth]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgraded = params.get("upgraded") === "1";
    const portalReturn = params.get("mock_portal") === "1";
    if (upgraded || portalReturn) {
      window.history.replaceState({}, "", "/settings/billing");
    }
    // Defer all setState calls (refetch + toast) outside the effect's commit phase.
    queueMicrotask(() => {
      refetch();
      if (upgraded) toast.success("Plan upgraded — welcome aboard");
      else if (portalReturn) toast.success("Returned from billing portal");
    });
  }, [refetch]);

  const onUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      const { billingApi } = await import("@/lib/api");
      const res = await billingApi.checkoutSession(planId);
      window.location.href = res.url;
    } catch (e) {
      toast.error(e?.message || "Could not start checkout");
      setUpgrading(null);
    }
  };

  const onOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const { billingApi } = await import("@/lib/api");
      const res = await billingApi.portalSession("/settings/billing");
      window.location.href = res.url;
    } catch (e) {
      toast.error(e?.message || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const currentPlan = plan?.plan || "trial";
  const isTrial = plan?.is_trial;
  const daysLeft = plan?.trial_days_left ?? 0;

  const stateBanner = isTrial ? (
    <div className="rounded-xl bg-indigo-50 ring-1 ring-indigo-200 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="billing-banner-trial">
      <Sparkles size={16} className="text-indigo-600 shrink-0" />
      <div className="flex-1 min-w-[200px]">
        <div className="text-[13px] font-semibold text-indigo-900">14-day trial · {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining</div>
        <div className="text-[11.5px] text-indigo-700">Upgrade to lock in features after the trial ends.</div>
      </div>
      <Button size="sm" className="h-8" onClick={() => onUpgrade("founder")} data-testid="billing-banner-upgrade">Upgrade now</Button>
    </div>
  ) : (
    <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="billing-banner-paid">
      <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
      <div className="flex-1 min-w-[200px]">
        <div className="text-[13px] font-semibold text-emerald-900">You&apos;re on the {plan?.plan_label || currentPlan} plan</div>
        <div className="text-[11.5px] text-emerald-700">{plan?.current_period_end ? `Renews ${new Date(plan.current_period_end).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })}` : ""}</div>
      </div>
      <Button size="sm" variant="outline" className="h-8" onClick={onOpenPortal} disabled={portalLoading} data-testid="billing-open-portal">{portalLoading ? "Opening…" : "Manage subscription"}</Button>
    </div>
  );

  return (
    <Shell title="Billing" desc="Plans, usage, and subscription.">
      <div className="p-6 space-y-4 max-w-5xl">
        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-[12.5px] text-slate-500" data-testid="billing-loading">Loading plan…</div>
        ) : (
          <>
            {stateBanner}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="billing-tiers">
              {(plan?.tiers || []).map((t) => {
                const isCurrent = t.id === currentPlan;
                return (
                  <div key={t.id} className={cn("rounded-xl border bg-white p-4 flex flex-col", isCurrent ? "border-indigo-300 ring-1 ring-indigo-200" : "border-slate-200")} data-testid={`billing-tier-${t.id}`}>
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{isCurrent ? "Current plan" : "Plan"}</div>
                    <div className="mt-1 text-[15px] font-semibold text-slate-900">{t.label}</div>
                    <div className="text-[13px] text-indigo-700 font-semibold tabular-nums">{billingTierPrice(t)}</div>
                    <div className="mt-1 text-[11px] text-slate-500 leading-snug flex-1">{t.description}</div>
                    <BillingTierCta tier={t} isCurrent={isCurrent} upgrading={upgrading} onUpgrade={onUpgrade} />
                  </div>
                );
              })}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="billing-payment-summary">
              <div className="text-[13px] font-semibold mb-2">Payment</div>
              <div className="text-[12px] text-slate-700">
                {plan?.has_payment_method ? (
                  <span>Mock Stripe customer on file</span>
                ) : (
                  <span className="text-slate-500 italic">No payment method on file — start an upgrade to attach one.</span>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-rose-200 bg-rose-50/30 p-4" data-testid="billing-danger-zone">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={14} className="text-rose-600" />
                <div className="text-[13px] font-semibold text-rose-900">Danger zone</div>
              </div>
              <p className="text-[11.5px] text-rose-700 mb-3">Delete this workspace and your account. Data is purged after a 30-day grace period.</p>
              <Button size="sm" variant="outline" className="h-8 text-rose-700 border-rose-300 hover:bg-rose-100" onClick={() => navigate("/settings/account")} data-testid="billing-go-delete">Delete account</Button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}

export function SettingsAccount() {
  const { signOut } = useAppStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);

  const canDelete = confirmation === "DELETE" && password.length >= 8 && !busy;

  const onConfirm = async () => {
    setBusy(true);
    try {
      const { authApi } = await import("@/lib/api");
      const r = await authApi.deleteAccount(password, confirmation);
      const when = r?.deletion_scheduled_at
        ? new Date(r.deletion_scheduled_at).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })
        : "soon";
      toast.success(`Account scheduled for deletion on ${when}. You've been signed out.`);
      setOpen(false);
      await signOut();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(e?.message || "Could not delete account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Account" desc="Password, sign out, and account management.">
      <div className="p-4 sm:p-6 space-y-4 max-w-3xl min-w-0" data-testid="account-settings-page">
        <ChangePasswordSection />

        <div className="rounded-xl border border-rose-200 bg-white p-4 sm:p-5" data-testid="account-danger-zone">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="text-rose-600 shrink-0" />
            <div className="text-[14px] font-semibold text-rose-900">Delete your account</div>
          </div>
          <p className="text-[12.5px] text-slate-700 mb-3 leading-relaxed">
            This soft-deletes your workspace and signs you out. You have <span className="font-semibold">30 days</span> to cancel deletion by logging back in. After that, your tenant database and all data are permanently purged — no recovery.
          </p>
          <Button size="sm" variant="outline" className="h-9 sm:h-8 text-rose-700 border-rose-300 hover:bg-rose-100 w-full sm:w-auto" onClick={() => setOpen(true)} data-testid="account-open-delete-modal">Delete account…</Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[460px] max-h-[90dvh] overflow-y-auto" data-testid="account-delete-modal">
          <DialogHeader><DialogTitle>Delete account?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 px-3 py-2 text-[12.5px] text-rose-800">
              <div className="font-semibold">This will:</div>
              <ul className="mt-1 list-disc list-inside space-y-0.5">
                <li>Pause Maya immediately and revoke all sessions</li>
                <li>Schedule permanent deletion in 30 days</li>
                <li>Allow cancellation by logging back in within the grace window</li>
              </ul>
            </div>
            <div>
              <label htmlFor="account-delete-password" className="text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold">Your password</label>
              <Input id="account-delete-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 sm:h-9 mt-1 w-full min-w-0" data-testid="account-delete-password" placeholder="Enter your password" />
            </div>
            <div>
              <label htmlFor="account-delete-confirmation" className="text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold">Type <span className="font-mono text-rose-700">DELETE</span> to confirm</label>
              <Input id="account-delete-confirmation" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} className="h-11 sm:h-9 mt-1 font-mono w-full min-w-0" data-testid="account-delete-confirmation" placeholder="DELETE" />
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <Button variant="outline" className="w-full sm:w-auto h-10 sm:h-9" onClick={() => setOpen(false)} data-testid="account-delete-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 w-full sm:w-auto h-10 sm:h-9" disabled={!canDelete} onClick={onConfirm} data-testid="account-delete-confirm">
              {busy ? "Deleting…" : "Delete my account"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function applyRoutingMemberFallback(rules, fallbackId) {
  return rules.map((r) => ({ ...r, member: r.member || fallbackId }));
}

function normalizeRoutingRules(rules, validIds, fallbackId) {
  return rules.map((r) => ({
    ...r,
    member: validIds.has(r.member) ? r.member : fallbackId,
  }));
}

function toRoutingMemberOptions(members) {
  return (members || []).map((m) => ({
    id: m.user_id,
    first: (m.display_name || m.email || "?").split(/\s+/)[0],
    name: m.display_name || m.email,
    title: m.role || "member",
    color: "from-indigo-500 to-violet-600",
  }));
}

export function SettingsRouting() {
  const { auth } = useAppStore();
  const [teamMembers, setTeamMembers] = useState([]);
  const defaultRules = [
    { id: "r1", trigger: "Pricing", member: "" },
    { id: "r2", trigger: "Product feedback", member: "" },
    { id: "r3", trigger: "Content collab", member: "" },
    { id: "r4", trigger: "Technical question", member: "" },
    { id: "r5", trigger: "Design feedback", member: "" },
  ];
  const [rules, setRules] = useState(defaultRules);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const memberOptions = toRoutingMemberOptions(teamMembers);

  const defaultMemberId = memberOptions[0]?.id || auth?.server?.user_id || "";

  const updateRule = (id, patch) => setRules((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRule = (id) => setRules((p) => p.filter((r) => r.id !== id));
  const addRule = () => setRules((p) => [...p, { id: `r-${Date.now()}`, trigger: "New topic", member: defaultMemberId }]);

  useEffect(() => {
    let cancelled = false;
    async function loadRouting() {
      try {
        const [membersRes, routingRes] = await Promise.all([
          teamApi.listMembers(),
          import("@/lib/api").then(({ routingApi }) => routingApi.get()),
        ]);
        if (cancelled) return;
        const options = toRoutingMemberOptions(membersRes.members);
        setTeamMembers(membersRes.members || []);
        const fallbackId = options[0]?.id || auth?.server?.user_id || "";
        if (Array.isArray(routingRes?.rules) && routingRes.rules.length) {
          const validIds = new Set(options.map((m) => m.id));
          setRules(normalizeRoutingRules(routingRes.rules, validIds, fallbackId));
        } else if (fallbackId) {
          setRules((prev) => applyRoutingMemberFallback(prev, fallbackId));
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Couldn't load routing rules");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadRouting();
    return () => { cancelled = true; };
  }, [auth?.server?.user_id]);

  const saveRules = async () => {
    setSaving(true);
    try {
      const { routingApi } = await import("@/lib/api");
      const r = await routingApi.save(rules);
      if (Array.isArray(r?.rules)) setRules(r.rules);
      toast.success("Routing rules saved · Maya will use them on the next inbound message");
    } catch (e) {
      toast.error(e?.message || "Couldn't save routing rules");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell title="Routing rules" desc="Tell Maya which teammate handles which kind of message.">
      <div className="p-6 max-w-3xl space-y-3" data-testid="settings-routing">
        {loading ? (
          <div className="text-[13px] text-slate-500 py-6">Loading routing rules…</div>
        ) : rules.map((r) => {
          const m = memberOptions.find((x) => x.id === r.member) || memberOptions[0];
          if (!m) {
            return (
              <div key={r.id} className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-600">
                Add team members before configuring routing rules.
              </div>
            );
          }
          return (
            <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-2" data-testid={`routing-rule-${r.id}`}>
              <span className="text-[11px] text-slate-500 font-semibold">When</span>
              <Input value={r.trigger} onChange={(e) => updateRule(r.id, { trigger: e.target.value })} className="h-8 flex-1 text-[12.5px]" data-testid={`routing-trigger-${r.id}`} />
              <span className="text-[11px] text-slate-500 font-semibold">→</span>
              <Select value={r.member} onValueChange={(member) => updateRule(r.id, { member })}>
                <SelectTrigger className="h-8 w-[180px] text-[12px]" data-testid={`routing-member-${r.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {memberOptions.map((x) => (
                    <SelectItem key={x.id} value={x.id} className="text-[12px]">
                      {`${x.first} · ${x.title}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="inline-flex items-center gap-1.5"><Avatar member={m} size={20} /><span className="text-[11.5px] text-slate-700">{m.first}</span></div>
              <Button size="sm" variant="ghost" className="h-7 text-[10.5px] text-rose-600" onClick={() => removeRule(r.id)} data-testid={`routing-delete-${r.id}`}>Remove</Button>
            </div>
          );
        })}
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8" onClick={addRule} data-testid="routing-add"><Plus size={12} /> Add rule</Button>
          <Button size="sm" className="h-8" onClick={saveRules} disabled={saving || loading} data-testid="routing-save">
            {saving ? "Saving…" : "Save rules"}
          </Button>
        </div>
      </div>
    </Shell>
  );
}

function fingerprintLabelFor(name, sampleCount) {
  if (sampleCount === 0) return `${name} hasn't learned your voice yet`;
  if (sampleCount === 1) return `${name} has a rough fingerprint`;
  if (sampleCount === 2) return `${name} is getting close`;
  return "Strong fingerprint";
}

async function hydratePersonaIntoStore(p, setters) {
  const {
    setName, setPronouns, setTone, setTagline, setSignature, setDoNotSay,
  } = setters;
  if (p.assistant_name) setName(p.assistant_name);
  if (p.pronouns) setPronouns(p.pronouns);
  if (p.tone_sliders && typeof p.tone_sliders === "object") {
    Object.entries(p.tone_sliders).forEach(([k, v]) => {
      if (typeof v === "number") setTone(k, v);
    });
  }
  if (p.signature_blocks && typeof p.signature_blocks === "object") {
    const sb = p.signature_blocks;
    if (typeof sb.tagline === "string") setTagline(sb.tagline);
    if (typeof sb.email === "string") setSignature("email", sb.email);
    if (typeof sb.linkedin === "string") setSignature("linkedin", sb.linkedin);
    if (typeof sb.whatsappEnabled === "boolean") setSignature("whatsappEnabled", sb.whatsappEnabled);
  }
  if (Array.isArray(p.do_not_say_phrases)) setDoNotSay(p.do_not_say_phrases);
}

function personaPreviewFields(editingHero, nameDraft, taglineDraft, name, tagline) {
  return {
    previewName: editingHero ? ((nameDraft || "").trim() || "Maya") : name,
    previewTagline: editingHero ? (taglineDraft || "").trim() : tagline,
  };
}

async function savePersonaPatch({
  canEdit, role, nameDraft, taglineDraft, pronouns, tones, signatures, doNotSay,
  setSaving, setName, setTagline, setNameDraft, setTaglineDraft, setEditingHero,
}) {
  if (!canEdit) { toast.error("Admins only — your role is " + role); return; }
  const cleanName = (nameDraft || "").trim() || "Maya";
  const cleanTagline = (taglineDraft || "").trim();
  setSaving(true);
  try {
    const { personaApi } = await import("@/lib/api");
    await personaApi.update({
      assistant_name: cleanName,
      pronouns,
      tone_sliders: tones,
      signature_blocks: {
        email: signatures.email,
        linkedin: signatures.linkedin,
        whatsappEnabled: signatures.whatsappEnabled,
        tagline: cleanTagline,
      },
      do_not_say_phrases: doNotSay,
    });
    setName(cleanName);
    setTagline(cleanTagline);
    setNameDraft(cleanName);
    setTaglineDraft(cleanTagline);
    setEditingHero(false);
    toast.success(`${cleanName}'s persona saved`);
  } catch (e) {
    if (e?.status === 403) {
      toast.error("Admins only — Save blocked");
    } else if (e?.status === 400 && e?.data?.detail?.error?.field) {
      const f = e.data.detail.error.field;
      toast.error(`Validation failed on ${f}: ${e.message}`);
    } else {
      toast.error(e?.message || "Failed to save persona");
    }
  } finally {
    setSaving(false);
  }
}

const PERSONA_WAVE_BARS = [
  { id: "wb0", h: 3 }, { id: "wb1", h: 6 }, { id: "wb2", h: 4 }, { id: "wb3", h: 8 },
  { id: "wb4", h: 5 }, { id: "wb5", h: 7 }, { id: "wb6", h: 3 }, { id: "wb7", h: 6 },
  { id: "wb8", h: 4 }, { id: "wb9", h: 5 }, { id: "wb10", h: 3 }, { id: "wb11", h: 7 },
];

const PERSONA_LOADING_KEYS = ["persona-skel-a", "persona-skel-b", "persona-skel-c"];

function getFingerprintLabel(sampleCount, name) {
  return fingerprintLabelFor(name, sampleCount);
}

function appendDoNotSayPhrase({ doNotSayDraft, doNotSay, setDoNotSay, setDoNotSayDraft }) {
  const v = doNotSayDraft.trim();
  if (!v) return;
  if (v.length > 280) { toast.error("Each phrase must be ≤ 280 characters"); return; }
  if (doNotSay.length >= 20) { toast.error("Capped at 20 phrases"); return; }
  if (doNotSay.includes(v)) { setDoNotSayDraft(""); return; }
  setDoNotSay((p) => [...p, v]);
  setDoNotSayDraft("");
}

function handlePersonaSaveSuccess(sync, cleanName) {
  if (sync?.status === "failed") {
    toast.error("Persona saved in Sellix, but OpenClaw sync failed", {
      description: sync.error || "Check the gateway connection and retry Save.",
    });
  } else if (sync?.status === "synced") {
    toast.success(`${cleanName}'s persona saved · OpenClaw agent synced`);
  } else {
    toast.success(`${cleanName}'s persona saved`);
  }
}

function handlePersonaSaveError(e) {
  if (e?.status === 403) {
    toast.error("Admins only — Save blocked");
  } else if (e?.status === 400 && e?.data?.detail?.error?.field) {
    toast.error(`Validation failed on ${e.data.detail.error.field}: ${e.message}`);
  } else {
    toast.error(e?.message || "Failed to save persona");
  }
}

async function executePersonaUpdate({
  canEdit, role, nameDraft, taglineDraft, pronouns, tones, signatures, doNotSay,
  setSaving, setName, setTagline, setNameDraft, setTaglineDraft, setEditingHero,
  avatarDraftFile = null,
  avatarPendingClear = false,
}) {
  if (!canEdit) { toast.error("Admins only — your role is " + role); return; }
  const cleanName = (nameDraft || "").trim();
  if (!cleanName) {
    toast.error("Assistant name is required — enter a name before saving.");
    return;
  }
  const cleanTagline = (taglineDraft || "").trim();
  setSaving(true);
  try {
    const { personaApi } = await import("@/lib/api");
    const { applyPersonaAvatarFromApi } = await import("@/pages/settings/personaHydration");

    let personaAfterAvatar = null;
    if (avatarPendingClear) {
      personaAfterAvatar = await personaApi.deleteAvatar();
    } else if (avatarDraftFile) {
      personaAfterAvatar = await personaApi.uploadAvatar(avatarDraftFile);
    }

    const patch = {
      assistant_name: cleanName,
      pronouns: pronouns,
      tone_sliders: tones,
      signature_blocks: {
        email: signatures.email,
        linkedin: signatures.linkedin,
        whatsappEnabled: signatures.whatsappEnabled,
        tagline: cleanTagline,
      },
      do_not_say_phrases: doNotSay,
    };

    const updated = await personaApi.update(patch);
    const merged = {
      ...updated,
      has_avatar: personaAfterAvatar?.has_avatar ?? updated.has_avatar,
      avatar_url: personaAfterAvatar?.avatar_url ?? updated.avatar_url,
      avatar_updated_at: personaAfterAvatar?.avatar_updated_at ?? updated.avatar_updated_at,
    };
    await applyPersonaAvatarFromApi(merged);
    setName(cleanName);
    setTagline(cleanTagline);
    setNameDraft(cleanName);
    setTaglineDraft(cleanTagline);
    setEditingHero(false);

    handlePersonaSaveSuccess(updated?.openclaw_sync, cleanName);
  } catch (e) {
    handlePersonaSaveError(e);
  } finally {
    setSaving(false);
  }
}

export function SettingsPersona() {
  const persona = usePersonaStore();
  const { name, tagline, pronouns, samples, tones, signatures,
    setName, setTagline, setPronouns, setSample, setTone, setSignature,
    snapshotAvatar, restoreAvatar } = persona;
  const { auth } = useAppStore();
  const role = auth?.server?.role || "owner";
  const canEdit = role === "owner" || role === "admin";
  const ownerFirstName = userFirstName(auth?.user);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doNotSay, setDoNotSay] = useState([]);
  const [doNotSayDraft, setDoNotSayDraft] = useState("");

  const [nameDraft, setNameDraft] = useState(name);
  const [taglineDraft, setTaglineDraft] = useState(tagline);
  const [editingHero, setEditingHero] = useState(false);
  const pronounsBeforeEditRef = useRef(pronouns);
  const avatarBeforeEditRef = useRef(null);
  const avatarDraftFileRef = useRef(null);

  const syncHeroDrafts = () => {
    setNameDraft(name);
    setTaglineDraft(tagline);
  };

  const startHeroEdit = () => {
    syncHeroDrafts();
    pronounsBeforeEditRef.current = pronouns;
    avatarBeforeEditRef.current = snapshotAvatar();
    avatarDraftFileRef.current = null;
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.info("[persona] entered edit mode");
    }
    setEditingHero(true);
  };

  const cancelHeroEdit = () => {
    syncHeroDrafts();
    if (pronounsBeforeEditRef.current !== pronouns) {
      setPronouns(pronounsBeforeEditRef.current);
    }
    if (avatarBeforeEditRef.current) {
      restoreAvatar(avatarBeforeEditRef.current);
      avatarBeforeEditRef.current = null;
    }
    avatarDraftFileRef.current = null;
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.info("[persona] edit cancelled — avatar restored");
    }
    setEditingHero(false);
  };

  useEffect(() => {
    if (!editingHero) syncHeroDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, tagline, editingHero]);

  // Hydrate from backend once on mount. Mirror values into the local persona store so the
  // existing rich UI (sample outputs, fingerprint, tour) stays reactive without rewrites.
  useEffect(() => {
    queueMicrotask(async () => {
      try {
        const { personaApi } = await import("@/lib/api");
        const { applyPersonaAvatarFromApi } = await import("@/pages/settings/personaHydration");
        const p = await personaApi.get();
        hydratePersonaIntoStore(p, {
          setName, setPronouns, setTone, setTagline, setSignature, setDoNotSay,
        });
        await applyPersonaAvatarFromApi(p);
      } catch (e) {
        toast.error(e?.message || "Couldn't load persona");
      } finally {
        setLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Roll counter for sample-output refresh — flip even/odd to choose roll
  const [rollMap, setRollMap] = useState({ whatsapp: 0, pricing: 0, linkedinConnect: 0, emailFollowup: 0 });
  const roll = (key) => setRollMap((m) => ({ ...m, [key]: (m[key] + 1) % 2 }));

  const bucket = bucketForTones(tones);
  const sampleFor = (key) => {
    const all = SAMPLE_BANK[key].filter((v) => v.variant === bucket);
    return all[rollMap[key] % all.length] || SAMPLE_BANK[key][0];
  };

  const sampleCount = voiceFingerprintSampleCount(samples);
  useEffect(() => {
    if (sampleCount < 3) return;
    const k = "sellix.persona.voiceFingerprintCelebrated";
    if (localStorage.getItem(k)) return;
    localStorage.setItem(k, "1");
    toast.success("Strong voice fingerprint locked in.", {
      description: `${name}'s outputs just got measurably more 'you'.`,
      duration: 5000,
    });
  }, [sampleCount, name]);
  const fingerprintPct = Math.round((sampleCount / 3) * 100);
  const fingerprintLabel = getFingerprintLabel(sampleCount, name);

  const previewName = editingHero ? ((nameDraft || "").trim() || name) : name;
  const previewTagline = editingHero ? (taglineDraft || "").trim() : tagline;

  const navigate = useNavigate();

  const samplesSection = [
    { key: "writingSamples", label: "Paste 3-5 of your best DMs or LinkedIn posts", placeholder: "e.g. Cold opener from Aug 14:\nHey Sarah — saw your post on...", successMsg: "Maya extracted 4 voice patterns from your writing.", type: "textarea", filled: sampleFieldFilled(samples.writingSamples) },
    { key: "audioSampleName", label: "Drop in an audio clip of yourself talking (~60s)", placeholder: "voice-sample.m4a", successMsg: "Maya detected: warm, direct, founder-friendly.", type: "audio", filled: sampleFieldFilled(samples.audioSampleName) },
    { key: "longFormSample", label: "Paste a long-form essay or blog post", placeholder: "Paste your founder essay or your most-read post here…", successMsg: "Vocabulary fingerprint: 1,247 distinct words extracted.", type: "textarea", filled: sampleFieldFilled(samples.longFormSample) },
  ];

  const tonesConfig = [
    { key: "formalCasual", left: "Formal", right: "Casual" },
    { key: "directDiplomatic", left: "Direct", right: "Diplomatic" },
    { key: "conciseDetailed", left: "Concise", right: "Detailed" },
    { key: "confidentHumble", left: "Confident", right: "Humble" },
    { key: "wittyEarnest", left: "Witty", right: "Earnest" },
  ];

  const addDoNotSay = () => appendDoNotSayPhrase({ doNotSayDraft, doNotSay, setDoNotSay, setDoNotSayDraft });
  const removeDoNotSay = (i) => setDoNotSay((p) => p.filter((_, idx) => idx !== i));

  const onSave = () => {
    const pendingClear = Boolean(usePersonaStore.getState().avatarPendingClear);
    executePersonaUpdate({
      canEdit, role, nameDraft, taglineDraft, pronouns, tones, signatures, doNotSay,
      setSaving, setName, setTagline, setNameDraft, setTaglineDraft, setEditingHero,
      avatarDraftFile: avatarDraftFileRef.current,
      avatarPendingClear: pendingClear,
    }).finally(() => {
      avatarDraftFileRef.current = null;
      avatarBeforeEditRef.current = null;
    });
  };

  const pronounsEditable = canEdit && editingHero;

  const onPronounChange = (next) => {
    if (!pronounsEditable || next === pronouns) return;
    setPronouns(next);
  };

  if (loading) {
    return (
      <Shell title="Persona" desc="Loading…">
        <div className="p-4 sm:p-6 max-w-5xl space-y-3" data-testid="persona-loading">
          {PERSONA_LOADING_KEYS.map((key) => (
            <div key={key} className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse">
              <div className="h-4 w-32 bg-slate-200 rounded mb-3" />
              <div className="h-3 w-full bg-slate-100 rounded mb-2" />
              <div className="h-3 w-5/6 bg-slate-100 rounded" />
            </div>
          ))}
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Persona" desc={`How ${name} sounds, signs off, and shows up as you.`}>
      <div className="grid grid-cols-12 gap-4 sm:gap-5 p-4 sm:p-6 max-w-7xl min-w-0">
        <div className={cn(SETTINGS_EXTENDED_FEATURES_ENABLED ? "col-span-12 lg:col-span-8" : "col-span-12", "space-y-5 min-w-0")}>

          {/* Hero — Meet your assistant */}
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-50/40 via-white to-white p-4 sm:p-5" data-testid="persona-hero">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> Meet your assistant</div>
              {canEdit && !editingHero && (
                <Button variant="outline" size="sm" className="h-9 sm:h-8 text-[11.5px]" onClick={startHeroEdit} data-testid="persona-hero-edit">
                  <Pencil size={12} /> Edit
                </Button>
              )}
              {canEdit && editingHero && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="ghost" size="sm" className="h-9 sm:h-8 text-[11.5px]" onClick={cancelHeroEdit} data-testid="persona-hero-cancel">
                    Cancel
                  </Button>
                  <Button size="sm" className="h-9 sm:h-8 text-[11.5px]" onClick={onSave} disabled={saving} data-testid="persona-hero-save">
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-4 sm:gap-5 flex-wrap">
              <PersonaAvatarButton
                name={previewName}
                disabled={!canEdit}
                editable={editingHero}
                onLockedClick={() => toast.message("Click Edit to modify your Persona.")}
                onFileChosen={(file) => {
                  avatarDraftFileRef.current = file;
                }}
                size={96}
              />
              <div className="flex-1 min-w-0 sm:min-w-[240px]">
                {editingHero ? (
                  <div className="space-y-2">
                    <Input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value.slice(0, 120))} className="text-[20px] sm:text-[22px] h-11 sm:h-10 font-semibold w-full max-w-[320px] min-w-0" data-testid="persona-name-input" placeholder="Assistant name" />
                    <Input value={taglineDraft} onChange={(e) => setTaglineDraft(e.target.value.slice(0, 200))} className="text-[13px] h-10 sm:h-8 w-full max-w-[320px] min-w-0" data-testid="persona-tagline-input" placeholder="Short description" />
                  </div>
                ) : (
                  <>
                    <div className="text-[22px] sm:text-[26px] font-semibold tracking-tight text-slate-900 leading-tight break-words" data-testid="persona-name">{name}</div>
                    <div className="text-[13px] text-slate-500 mt-0.5 break-words" data-testid="persona-tagline">{tagline || "Your AI SDR"}</div>
                  </>
                )}
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mr-0.5">Pronouns</span>
                  {["she/her", "he/him", "they/them"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onPronounChange(p)}
                      disabled={!pronounsEditable}
                      aria-pressed={pronouns === p}
                      className={cn(
                        "text-[10.5px] rounded-full px-2 py-1.5 sm:py-0.5 min-h-8 sm:min-h-0 ring-1 font-medium transition-colors",
                        pronouns === p ? "bg-indigo-50 text-indigo-700 ring-indigo-200" : "bg-white text-slate-600 ring-slate-200",
                        pronounsEditable ? "hover:bg-slate-50" : "opacity-60 cursor-not-allowed",
                      )}
                      data-testid={`persona-pronoun-${p.replace("/", "-")}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-lg bg-white ring-1 ring-slate-200 px-3 py-2.5 text-[12px] text-slate-700 leading-snug w-full sm:w-auto sm:max-w-[260px]">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Live preview</div>
                <span className="break-words">&ldquo;Hello, I&apos;m <span className="font-semibold text-slate-900">{previewName}</span>. I work with {ownerFirstName} as <span className="font-medium">{previewTagline || "your AI SDR"}</span>.&rdquo;</span>
              </div>
            </div>
          </div>

          {SETTINGS_EXTENDED_FEATURES_ENABLED && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-start gap-3 justify-between flex-wrap">
                <div>
                  <div className="text-[14px] font-semibold text-slate-900">Voice &amp; writing samples</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">Show {name} a few examples of how you sound. The more she sees, the better she writes.</div>
                </div>
                <div className="min-w-[180px]">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center justify-between">
                    <span>How sure is {name}?</span>
                    <span className="text-indigo-700 tabular-nums">{fingerprintPct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={cn("h-full transition-all duration-500", sampleCount >= 3 ? "bg-gradient-to-r from-emerald-400 to-emerald-600" : "bg-gradient-to-r from-indigo-400 to-violet-500")} style={{ width: `${fingerprintPct}%` }} />
                  </div>
                  <div className="text-[10.5px] text-slate-600 mt-0.5">{fingerprintLabel}</div>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {samplesSection.map((s) => (
                  <div key={s.key} className={cn("rounded-lg border p-3", s.filled ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white")} data-testid={`persona-sample-${s.key}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="text-[12px] font-semibold text-slate-800">{s.label}</div>
                      {s.filled && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold"><CheckCircle2 size={10} /> Added</span>}
                    </div>
                    {s.type === "textarea" ? (
                      <Textarea
                        rows={3}
                        value={samples[s.key] || ""}
                        onChange={(e) => setSample(s.key, e.target.value)}
                        placeholder={s.placeholder}
                        className="text-[12px] resize-none"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        {samples.audioSampleName ? (
                          <div className="flex-1 flex items-center gap-2 rounded-md bg-white ring-1 ring-slate-200 px-2.5 py-1.5">
                            <Activity size={12} className="text-indigo-600" />
                            <span className="text-[11.5px] text-slate-700 font-mono">{samples.audioSampleName}</span>
                            <div className="ml-auto flex items-end gap-0.5 h-4">
                              {PERSONA_WAVE_BARS.map((bar) => (
                                <div key={bar.id} className="w-0.5 bg-indigo-400 rounded-sm" style={{ height: `${bar.h * 2}px` }} />
                              ))}
                            </div>
                          </div>
                        ) : (
                          <Button variant="outline" size="sm" className="h-8 text-[11.5px]" onClick={() => { setSample("audioSampleName", "voice-sample.m4a"); toast.success(s.successMsg); }} data-testid="persona-audio-upload">
                            <Mic size={11} /> Drop voice clip here
                          </Button>
                        )}
                      </div>
                    )}
                    {s.filled && s.type === "textarea" && (
                      <div className="mt-1.5 text-[10.5px] text-emerald-700">{s.successMsg}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {SETTINGS_EXTENDED_FEATURES_ENABLED && (
            <>
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="text-[14px] font-semibold text-slate-900">Tone</div>
                <div className="text-[12px] text-slate-500 mt-0.5 mb-4">Dial {name}&apos;s voice. Sample outputs on the right update as you drag.</div>
                <div className="space-y-4">
                  {tonesConfig.map((t) => (
                    <div key={t.key} data-testid={`persona-tone-${t.key}`}>
                      <div className="flex items-center justify-between text-[11px] mb-1.5">
                        <span className="font-medium text-slate-700">{t.left}</span>
                        <span className="text-slate-400 tabular-nums">{tones[t.key]}</span>
                        <span className="font-medium text-slate-700">{t.right}</span>
                      </div>
                      <Slider
                        value={[tones[t.key]]}
                        onValueChange={(v) => setTone(t.key, v[0])}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Do-not-say phrases (Sprint 4.A field) */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="persona-do-not-say">
                <div className="text-[14px] font-semibold text-slate-900">Things {name} should never say</div>
                <div className="text-[12px] text-slate-500 mt-0.5 mb-3">Phrases or claims that conflict with your brand. {name} will avoid these in every channel. Max 20 · 280 chars each.</div>
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    value={doNotSayDraft}
                    onChange={(e) => setDoNotSayDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addDoNotSay();
                      }
                    }}
                    placeholder='e.g. "guaranteed ROI"'
                    className="h-8 flex-1 text-[12px]"
                    maxLength={280}
                    disabled={!canEdit}
                    data-testid="persona-dns-input"
                  />
                  <Button size="sm" className="h-8" onClick={addDoNotSay} disabled={!canEdit || !doNotSayDraft.trim()} data-testid="persona-dns-add">Add</Button>
                  <span className="text-[10.5px] text-slate-500 tabular-nums">{doNotSay.length}/20</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {doNotSay.length === 0 && <span className="text-[11px] text-slate-400 italic">No phrases yet — {name} can say anything brand-safe.</span>}
                  {doNotSay.map((p) => (
                    <span key={p} className="inline-flex items-center gap-1 rounded-full bg-rose-50 ring-1 ring-rose-200 text-rose-700 px-2 py-0.5 text-[11px]" data-testid={`persona-dns-chip-${p}`}>
                      {p}
                      {canEdit && (
                        <button onClick={() => removeDoNotSay(doNotSay.indexOf(p))} className="text-rose-500 hover:text-rose-700" aria-label="Remove">×</button>
                      )}
                    </span>
                  ))}
                </div>
              </div>

              {/* Signatures (persisted via signature_blocks jsonb) */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="text-[14px] font-semibold text-slate-900">Signatures</div>
                <div className="text-[12px] text-slate-500 mt-0.5 mb-4">How {name} signs off — per channel.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="persona-sig-email" className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5"><Mail size={10} /> Email signature</label>
                    <Textarea id="persona-sig-email" rows={3} value={signatures.email} onChange={(e) => setSignature("email", e.target.value)} className="mt-1 text-[12px] resize-none" data-testid="persona-sig-email" />
                  </div>
                  <div>
                    <label htmlFor="persona-sig-linkedin" className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5"><Linkedin size={10} /> LinkedIn sign-off</label>
                    <Input id="persona-sig-linkedin" value={signatures.linkedin} onChange={(e) => setSignature("linkedin", e.target.value)} className="mt-1 text-[12px] h-9" data-testid="persona-sig-linkedin" />
                  </div>
                  <div className="md:col-span-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg ring-1 ring-slate-200 bg-slate-50/50 px-3 py-2.5 sm:py-2">
                    <div className="min-w-0">
                      <div className="text-[12px] font-medium text-slate-800 inline-flex items-center gap-1.5"><MessageCircle size={12} className="text-emerald-600" /> Sign WhatsApp messages</div>
                      <div className="text-[10.5px] text-slate-500 mt-0.5">Most founders don&apos;t sign WhatsApp. Off by default.</div>
                    </div>
                    <Switch checked={signatures.whatsappEnabled} onCheckedChange={(v) => setSignature("whatsappEnabled", v)} data-testid="persona-sig-wa" className="self-start sm:self-auto" />
                  </div>
                </div>
              </div>
            </>
          )}

          {SETTINGS_EXTENDED_FEATURES_ENABLED && (
            <div className="sticky bottom-2 flex justify-end rounded-xl border border-slate-200 bg-white shadow-sm px-3 sm:px-4 py-3 min-w-0" data-testid="persona-action-bar">
              <Button variant="outline" size="sm" className="h-9 sm:h-8 text-[11.5px]" onClick={() => navigate("/content/studio")} data-testid="persona-test-studio">Test {name} in Studio →</Button>
            </div>
          )}
        </div>

        {SETTINGS_EXTENDED_FEATURES_ENABLED && (
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-4 space-y-3">
              <div className="rounded-xl bg-slate-900 text-white p-4">
                <div className="text-[10.5px] uppercase tracking-wider text-indigo-300 font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> Sample outputs</div>
                <div className="text-[11.5px] text-slate-300 mt-1">How {name} would write right now.</div>
              </div>
              {[
                { key: "whatsapp", title: "Cold WhatsApp opener", Icon: MessageCircle, color: "text-emerald-600" },
                { key: "pricing", title: "Pricing-question reply", Icon: MessageSquare, color: "text-indigo-600" },
                { key: "linkedinConnect", title: "LinkedIn connection-request note", Icon: Linkedin, color: "text-sky-600" },
                { key: "emailFollowup", title: "Email follow-up after no reply", Icon: Mail, color: "text-rose-600" },
              ].map((s) => {
                const out = sampleFor(s.key);
                return (
                  <div key={s.key} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`persona-out-${s.key}`}>
                    <div className="flex items-center gap-1.5">
                      <s.Icon size={12} className={s.color} />
                      <div className="text-[11px] font-semibold text-slate-800">{s.title}</div>
                      <button onClick={() => roll(s.key)} className="ml-auto text-[10px] text-indigo-600 hover:text-indigo-700 font-medium" data-testid={`persona-out-roll-${s.key}`}>Refresh</button>
                    </div>
                    <div className="mt-1.5 text-[11.5px] text-slate-700 leading-relaxed whitespace-pre-line">{out.text}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

const LEAD_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LEAD_NAME_MAX_LEN = 120;

export function SettingsGeneral() {
  const { auth } = useAppStore();
  const role = auth?.server?.role || "owner";
  const canEdit = role === "owner" || role === "admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSend, setAutoSend] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadNameError, setLeadNameError] = useState("");
  const [leadEmailError, setLeadEmailError] = useState("");
  const [leadTemplate, setLeadTemplate] = useState("");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  const validateLeadFields = (nameValue, emailValue) => {
    const trimmedEmail = emailValue.trim();
    const trimmedName = nameValue.trim();
    let nameErr = "";
    let emailErr = "";
    if (trimmedEmail && (/\s/.test(trimmedEmail) || !LEAD_EMAIL_RE.test(trimmedEmail))) {
      emailErr = "Enter a valid email address";
    }
    if (trimmedEmail && !trimmedName) {
      nameErr = "Name is required when an email address is set";
    } else if (trimmedName.length > LEAD_NAME_MAX_LEN) {
      nameErr = `Name must be ${LEAD_NAME_MAX_LEN} characters or fewer`;
    }
    return { nameErr, emailErr };
  };

  useEffect(() => {
    let cancelled = false;
    async function loadGeneral() {
      try {
        const { generalSettingsApi } = await import("@/lib/api");
        const s = await generalSettingsApi.get();
        if (cancelled) return;
        setAutoSend(!!s.auto_send_ai_replies);
        setLeadName(s.lead_notification_name || "");
        setLeadEmail(s.lead_notification_email || "");
        setLeadTemplate(s.lead_notification_email_template || "");
        setLeadNameError("");
        setLeadEmailError("");
        setDirty(false);
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Couldn't load general settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadGeneral();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    if (window.location.hash !== "#lead-notification") return undefined;
    const t = window.setTimeout(() => {
      document.getElementById("lead-notification")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [loading]);

  const onToggleAutoSend = async (v) => {
    const newValue = !!v;
    setAutoSend(newValue);
    setDirty(true);
    
    // Auto-save the toggle immediately
    if (!canEdit) return;
    
    try {
      const { generalSettingsApi } = await import("@/lib/api");
      const updated = await generalSettingsApi.update({
        auto_send_ai_replies: newValue,
      });
      setAutoSend(!!updated.auto_send_ai_replies);
      toast.success(`Automatically Send AI Replies ${newValue ? "enabled" : "disabled"}`);
      // Keep dirty state for other fields that may have changed
      const trimmedEmail = leadEmail.trim();
      const trimmedName = leadName.trim();
      const hasLeadChanges = 
        trimmedEmail !== (updated.lead_notification_email || "") ||
        trimmedName !== (updated.lead_notification_name || "");
      if (!hasLeadChanges) {
        setDirty(false);
      }
    } catch (e) {
      // Revert on error
      setAutoSend(!newValue);
      if (e?.status === 403) toast.error("Admins only");
      else toast.error(e?.message || "Failed to save auto-send setting");
    }
  };

  const onLeadNameChange = (value) => {
    setLeadName(value);
    setDirty(true);
    const { nameErr, emailErr } = validateLeadFields(value, leadEmail);
    setLeadNameError(nameErr);
    setLeadEmailError(emailErr);
  };

  const onLeadEmailChange = (value) => {
    setLeadEmail(value);
    setDirty(true);
    const { nameErr, emailErr } = validateLeadFields(leadName, value);
    setLeadNameError(nameErr);
    setLeadEmailError(emailErr);
  };

  const handleGeneralSettingsError = (e) => {
    if (e?.status === 403) toast.error("Admins only");
    else if (e?.status === 400) toast.error(e?.message || "Validation failed");
    else toast.error(e?.message || "Failed to save general settings");
  };

  const onSave = async () => {
    if (!canEdit) {
      toast.error("Admins only — your role is " + role);
      return;
    }
    const trimmedEmail = leadEmail.trim();
    const trimmedName = leadName.trim();
    const { nameErr, emailErr } = validateLeadFields(trimmedName, trimmedEmail);
    if (nameErr || emailErr) {
      setLeadNameError(nameErr);
      setLeadEmailError(emailErr);
      toast.error(nameErr || emailErr || "Fix validation errors before saving");
      return;
    }
    setSaving(true);
    try {
      const { generalSettingsApi } = await import("@/lib/api");
      const updated = await generalSettingsApi.update({
        auto_send_ai_replies: autoSend,
        lead_notification_name: trimmedName || null,
        lead_notification_email: trimmedEmail || null,
      });
      setAutoSend(!!updated.auto_send_ai_replies);
      setLeadName(updated.lead_notification_name || "");
      setLeadEmail(updated.lead_notification_email || "");
      setLeadTemplate(updated.lead_notification_email_template || leadTemplate);
      setLeadNameError("");
      setLeadEmailError("");
      setDirty(false);
      toast.success("General settings saved");
      try {
        window.dispatchEvent(new Event("sellyx:lead-notification-updated"));
      } catch { /* ignore */ }
    } catch (e) {
      handleGeneralSettingsError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell
      title="General Settings"
      desc="Workspace-wide behaviour for AI replies and lead notifications."
    >
      <div className="max-w-2xl px-4 sm:px-6 py-5 space-y-5" data-testid="general-settings-page">
        {loading ? (
          <p className="text-[13px] text-slate-500">Loading…</p>
        ) : (
          <>
            <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-slate-900">
                    Automatically Send AI Replies
                  </div>
                  <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                    When enabled, AI-generated replies for inbound Email, WhatsApp and Telegram
                    messages will be sent automatically without requiring user approval.
                    Leave this off to keep Approve &amp; Send as the default.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11.5px] font-medium text-slate-500">
                    {autoSend ? "ON" : "OFF"}
                  </span>
                  <Switch
                    checked={autoSend}
                    disabled={!canEdit}
                    onCheckedChange={onToggleAutoSend}
                    data-testid="auto-send-ai-replies-toggle"
                  />
                </div>
              </div>
            </section>

            <section
              id="lead-notification"
              className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 scroll-mt-4"
              data-testid="lead-notification-section"
            >
              <div className="text-[14px] font-semibold text-slate-900">
                Lead Notification
              </div>
              <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                Qualified opportunities will trigger an email notification to the recipient below.
                Leave email blank to skip notifications.
              </p>
              <div className="mt-4 space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-notification-name" className="text-[12px] text-slate-700">
                    Name
                  </Label>
                  <Input
                    id="lead-notification-name"
                    value={leadName}
                    disabled={!canEdit}
                    placeholder="Sales Team"
                    onChange={(e) => onLeadNameChange(e.target.value)}
                    className={cn(leadNameError && "border-rose-300 focus-visible:ring-rose-200")}
                    data-testid="lead-notification-name-input"
                  />
                  {leadNameError ? (
                    <p className="text-[11.5px] text-rose-600" data-testid="lead-name-error">
                      {leadNameError}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lead-notification-email" className="text-[12px] text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="lead-notification-email"
                    type="email"
                    value={leadEmail}
                    disabled={!canEdit}
                    placeholder="sales@company.com"
                    onChange={(e) => onLeadEmailChange(e.target.value)}
                    className={cn(leadEmailError && "border-rose-300 focus-visible:ring-rose-200")}
                    data-testid="lead-notification-email-input"
                  />
                  {leadEmailError ? (
                    <p className="text-[11.5px] text-rose-600" data-testid="lead-email-error">
                      {leadEmailError}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2" data-testid="lead-notification-template-status">
                    <span className="text-[11.5px] font-medium uppercase tracking-wide text-slate-500">
                      Status
                    </span>
                    {leadTemplate ? (
                      <span
                        className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11.5px] font-medium text-emerald-700 ring-1 ring-emerald-200"
                        data-testid="lead-notification-template-configured"
                      >
                        ✓ 1 Template Configured
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11.5px] font-medium text-slate-500 ring-1 ring-slate-200"
                        data-testid="lead-notification-template-missing"
                      >
                        No template configured
                      </span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canEdit}
                    onClick={() => setTemplateModalOpen(true)}
                    data-testid="lead-notification-set-template"
                  >
                    Set Email Template
                  </Button>
                </div>
              </div>
            </section>

            <LeadNotificationTemplateModal
              open={templateModalOpen}
              onOpenChange={setTemplateModalOpen}
              initialTemplate={leadTemplate}
              canEdit={canEdit}
              onSaved={(html) => setLeadTemplate(html)}
            />

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                type="button"
                disabled={!canEdit || saving || !dirty || !!leadEmailError || !!leadNameError}
                onClick={onSave}
                data-testid="general-settings-save"
              >
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}

export function SettingsConnections() {
  const conn = useConnectionsSettings();
  return <ConnectionsSettingsView Shell={Shell} conn={conn} />;
}

function channelCardBorderClass(status) {
  if (status === "pairing") return "border-indigo-300 ring-1 ring-indigo-200";
  return "border-slate-200";
}

// Legacy ChannelCard / StatusBadge retained below (used by other surfaces in the app).
function ChannelCard({ id, channel, meta, onPair, onDisconnect, onPromoteLi, onRemoveLi }) {
  const I = meta.Icon;
  const status = channel.status;
  return (
    <div
      className={cn(
        "rounded-xl border bg-white p-4 transition-shadow hover:shadow-sm",
        channelCardBorderClass(status),
      )}
      data-testid={`channel-card-${id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", meta.iconBg)}>
          <I size={17} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13.5px] font-semibold text-slate-900">{channel.label}</div>
            <StatusBadge status={status} />
          </div>

          {/* Connected detail */}
          {status === "connected" && id !== "linkedin" && (
            <div className="mt-1.5 text-[11.5px] text-slate-600 space-y-0.5">
              {channel.identity && <div className="font-mono text-slate-700">{channel.identity}</div>}
              {channel.device && <div className="text-slate-500">{channel.device}</div>}
              {channel.inboxScope && <div className="text-slate-500">{channel.inboxScope}</div>}
              {channel.botUsername && <div className="font-mono text-slate-700">{channel.botUsername}</div>}
              <div className="text-[10.5px] text-slate-400 inline-flex items-center gap-1.5 pt-0.5">
                <CheckCircle2 size={10} className="text-emerald-500" />
                {channel.pairedAt}
                {channel.lastSync && <><span>·</span><span>last sync {channel.lastSync}</span></>}
                {typeof channel.messagesToday === "number" && <><span>·</span><span><span className="font-semibold text-slate-700 tabular-nums">{channel.messagesToday}</span> today</span></>}
              </div>
            </div>
          )}

          {/* LinkedIn — show pool */}
          {status === "connected" && id === "linkedin" && (
            <div className="mt-2 space-y-1">
              {(channel.pool || []).map((a) => (
                <div key={a.id} className="flex items-center gap-2 text-[11.5px] rounded-md border border-slate-200/70 bg-slate-50/40 px-2 py-1.5">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 text-white text-[9px] font-semibold shrink-0">{a.avatar}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 truncate">
                      {a.name}
                      {a.primary && <span className="ml-1.5 inline-block rounded-full bg-indigo-100 text-indigo-700 px-1.5 py-0 text-[9px] font-semibold align-middle">primary</span>}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500 truncate">{a.handle}</div>
                  </div>
                  <div className="text-[10px] text-slate-500 tabular-nums shrink-0 hidden md:block">{a.dailyUsed}/{a.dailyLimit} today · safety {a.safety}</div>
                  {!a.primary && (
                    <button onClick={() => onPromoteLi(a.id)} className="text-[10px] text-indigo-600 hover:text-indigo-700 font-medium shrink-0" data-testid={`li-promote-${a.id}`}>Set primary</button>
                  )}
                  {(channel.pool.length > 1) && (
                    <button onClick={() => onRemoveLi(a.id)} className="text-[10px] text-rose-600 hover:text-rose-700 font-medium shrink-0" data-testid={`li-remove-${a.id}`}>Remove</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Disconnected blurb */}
          {status !== "connected" && (
            <div className="mt-1.5 text-[11.5px] text-slate-500 leading-snug">{meta.blurb}</div>
          )}

          {status === "pairing" && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-indigo-50 ring-1 ring-indigo-200 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" /> Pairing in progress…
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          {status === "connected" ? (
            <>
              {id === "linkedin" && (
                <Button size="sm" variant="outline" className="h-7 text-[10.5px]" onClick={onPair} data-testid={`pair-${id}-add`}>
                  <Plus size={11} /> Add account
                </Button>
              )}
              <button
                onClick={onDisconnect}
                className="text-[10.5px] font-medium text-slate-500 hover:text-rose-600 transition-colors"
                data-testid={`disconnect-${id}`}
              >
                Disconnect
              </button>
            </>
          ) : (
            <Button size="sm" className="h-8 text-[11.5px]" onClick={onPair} data-testid={`pair-${id}`}>
              {status === "pairing" ? "Resume" : "Pair"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

ChannelCard.propTypes = {
  id: PropTypes.string,
  channel: PropTypes.shape({
    status: PropTypes.string,
    label: PropTypes.string,
    identity: PropTypes.string,
    device: PropTypes.string,
    inboxScope: PropTypes.string,
    botUsername: PropTypes.string,
    pairedAt: PropTypes.string,
    lastSync: PropTypes.string,
    messagesToday: PropTypes.number,
    pool: PropTypes.array,
  }),
  meta: PropTypes.shape({
    Icon: PropTypes.elementType,
    iconBg: PropTypes.string,
    blurb: PropTypes.string,
  }),
  onPair: PropTypes.func,
  onDisconnect: PropTypes.func,
  onPromoteLi: PropTypes.func,
  onRemoveLi: PropTypes.func,
};

function StatusBadge({ status }) {
  if (status === "connected") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Connected
    </span>
  );
  if (status === "pairing") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 px-1.5 py-0.5 text-[10px] font-semibold">
      <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" /> Pairing
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 ring-1 ring-slate-200 px-1.5 py-0.5 text-[10px] font-semibold">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Not connected
    </span>
  );
}

StatusBadge.propTypes = {
  status: PropTypes.string,
};

export function SettingsSafety() {
  const { auth } = useAppStore();
  const role = auth?.server?.role || "owner";
  const canEdit = role === "owner" || role === "admin";
  const getAccountLabel = useConnectionsStore((s) => s.getAccountLabel);
  const tosByChannel = useSafetyStore((s) => s.tosByChannel);
  const setTosForChannel = useSafetyStore((s) => s.setTosForChannel);

  // ─── Backend-backed safety config ─────────────────────────────────────────
  const [serverSafety, setServerSafety] = useState(null);
  const [loadingSafety, setLoadingSafety] = useState(true);
  const [savingSafety, setSavingSafety] = useState(false);
  const [warmupPreset, setWarmupPreset] = useState("normal");
  const [timezone, setTimezone] = useState("America/Los_Angeles");
  const [workingStart, setWorkingStart] = useState("09:00");
  const [workingEnd, setWorkingEnd] = useState("18:00");
  const [dmConfidencePct, setDmConfidencePct] = useState(65);
  const [redflagDraft, setRedflagDraft] = useState("");
  const [redflagTerms, setRedflagTerms] = useState([]);
  const [escalationDraft, setEscalationDraft] = useState("");
  const [escalationEmails, setEscalationEmails] = useState([]);

  useEffect(() => {
    queueMicrotask(async () => {
      try {
        const { safetyApi } = await import("@/lib/api");
        const s = await safetyApi.get();
        setServerSafety(s);
        if (s.warmup_preset) setWarmupPreset(s.warmup_preset);
        if (s.timezone) setTimezone(s.timezone);
        if (s.working_hours_start) setWorkingStart(s.working_hours_start);
        if (s.working_hours_end) setWorkingEnd(s.working_hours_end);
        if (typeof s.dm_confidence_threshold === "number") setDmConfidencePct(Math.round(s.dm_confidence_threshold * 100));
        if (Array.isArray(s.redflag_terms)) setRedflagTerms(s.redflag_terms);
        if (Array.isArray(s.escalation_emails)) setEscalationEmails(s.escalation_emails);
      } catch (e) {
        toast.error(e?.message || "Couldn't load safety config");
      } finally {
        setLoadingSafety(false);
      }
    });
  }, []);

  const addRedflag = () => {
    const v = redflagDraft.trim();
    if (!v) return;
    if (v.length > 100) { toast.error("Each red-flag term ≤ 100 chars"); return; }
    if (redflagTerms.length >= 50) { toast.error("Capped at 50 terms"); return; }
    if (redflagTerms.includes(v)) { setRedflagDraft(""); return; }
    setRedflagTerms((p) => [...p, v]);
    setRedflagDraft("");
  };
  const removeRedflag = (i) => setRedflagTerms((p) => p.filter((_, idx) => idx !== i));

  const addEscalation = () => {
    const v = escalationDraft.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error("Invalid email"); return; }
    if (escalationEmails.length >= 10) { toast.error("Capped at 10 escalation emails"); return; }
    if (escalationEmails.includes(v)) { setEscalationDraft(""); return; }
    setEscalationEmails((p) => [...p, v]);
    setEscalationDraft("");
  };
  const removeEscalation = (i) => setEscalationEmails((p) => p.filter((_, idx) => idx !== i));

  const onSaveSafety = async () => {
    if (!canEdit) { toast.error("Admins only — your role is " + role); return; }
    setSavingSafety(true);
    try {
      const { safetyApi } = await import("@/lib/api");
      const updated = await safetyApi.update({
        warmup_preset: warmupPreset,
        timezone: timezone,
        working_hours_start: workingStart,
        working_hours_end: workingEnd,
        dm_confidence_threshold: dmConfidencePct / 100,
        redflag_terms: redflagTerms,
        escalation_emails: escalationEmails,
      });
      setServerSafety(updated);
      toast.success("Safety configuration saved");
    } catch (e) {
      if (e?.status === 403) toast.error("Admins only");
      else if (e?.status === 400 && e?.data?.detail?.error?.field) {
        toast.error(`Validation failed on ${e.data.detail.error.field}: ${e.message}`);
      } else toast.error(e?.message || "Failed to save safety config");
    } finally {
      setSavingSafety(false);
    }
  };

  // Per-channel ban-risk gauges (mocked but believable)
  const gauges = [
    { id: "whatsapp", Icon: MessageCircle, score: 22, velocity: 47, capDaily: 80, complaint: 0, diversity: 92, sentence: "Maya is sending at ~59% of your cap. Recipient mix is healthy." },
    { id: "linkedin", Icon: Linkedin, score: 31, velocity: 14, capDaily: 40, complaint: 0, diversity: 88, sentence: "Daily volume is well within human bounds. Connection acceptance: 64%." },
    { id: "gmail", Icon: Mail, score: 18, velocity: 23, capDaily: 200, complaint: 0.1, diversity: 96, sentence: "Inbox reputation strong. No bounces or complaints in 30 days." },
    { id: "telegram", Icon: Send, score: 15, velocity: 0, capDaily: 100, complaint: 0, diversity: 0, sentence: "Bot live · no broadcasts queued. Maya stays well under the soft limit." },
    { id: "sms", Icon: MessageSquare, score: 35, velocity: 12, capDaily: 30, complaint: 0, diversity: 84, sentence: "SMS is your highest-risk channel — Maya keeps daily volume conservative." },
  ];

  // Map gauge.id to connections-store id (sms has no channel match)
  const channelKeyMap = { whatsapp: "whatsapp", linkedin: "linkedin", gmail: "gmail", telegram: "telegram", sms: null };

  const [coolingDown, setCoolingDown] = useState({}); // map id → bool

  const ringColor = (score) => {
    if (score <= 30) return "#10B981";
    if (score <= 70) return "#F59E0B";
    return "#F43F5E";
  };
  const ringTint = (score) => {
    if (score <= 30) return "bg-emerald-50 ring-emerald-200 text-emerald-700";
    if (score <= 70) return "bg-amber-50 ring-amber-200 text-amber-700";
    return "bg-rose-50 ring-rose-200 text-rose-700";
  };

  // Warmup schedules (mock)
  const warmups = [
    { account: "Aman's Gmail (newly paired)", current: 23, target: 100, daysIn: 4, daysLeft: 10, ramp: "12% / day" },
    { account: "Aman's LinkedIn (#2 in pool)", current: 8, target: 40, daysIn: 2, daysLeft: 14, ramp: "8% / day" },
  ];

  const [alerts, setAlerts] = useState({
    banRisk: { on: true, channel: "Email" },
    pattern: { on: true, channel: "WhatsApp DM" },
    disco: { on: true, channel: "All" },
    cooldown: { on: false, channel: "Slack" },
  });
  const setAlertOn = (key, v) => setAlerts((s) => ({ ...s, [key]: { ...s[key], on: v } }));
  const setAlertChannel = (key, ch) => setAlerts((s) => ({ ...s, [key]: { ...s[key], channel: ch } }));

  return (
    <Shell title="Safety" desc="Maya works hard to keep your channels safe from bans and rate limits.">
      <div className="p-6 max-w-6xl space-y-6">

        {/* Safety configuration (Sprint 4.A backend) */}
        {loadingSafety ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse" data-testid="safety-config-loading">
            <div className="h-4 w-40 bg-slate-200 rounded mb-3" />
            <div className="h-3 w-full bg-slate-100 rounded mb-2" />
            <div className="h-3 w-3/4 bg-slate-100 rounded" />
          </div>
        ) : (
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="safety-config-card">
            <div className="flex items-start gap-3 justify-between flex-wrap">
              <div>
                <div className="text-[15px] font-semibold text-slate-900 inline-flex items-center gap-2"><ShieldCheck size={15} className="text-indigo-600" /> Safety configuration</div>
                <p className="text-[12px] text-slate-500 mt-0.5">Server-persisted guardrails. Maya consults these on every outbound message.</p>
              </div>
              {serverSafety?.updated_at && (
                <span className="text-[10.5px] text-slate-400">last saved {new Date(serverSafety.updated_at).toLocaleString()}</span>
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Warmup preset */}
              <div>
                <fieldset>
                  <legend className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Warmup preset</legend>
                  <div className="mt-1 flex flex-wrap gap-1.5" data-testid="safety-warmup">
                    {["conservative", "normal", "aggressive", "custom"].map((p) => (
                      <button
                        key={p}
                        type="button"
                        disabled={!canEdit}
                        onClick={() => setWarmupPreset(p)}
                        className={cn("text-[11px] rounded-full px-2.5 py-0.5 ring-1 capitalize transition-colors", warmupPreset === p ? "bg-indigo-50 text-indigo-700 ring-indigo-300 font-semibold" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50", !canEdit && "opacity-50 cursor-not-allowed")}
                        data-testid={`safety-warmup-${p}`}
                      >{p}</button>
                    ))}
                  </div>
                </fieldset>
              </div>

              {/* DM confidence threshold */}
              <div>
                <fieldset>
                  <legend className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between w-full">
                    <span>DM confidence threshold</span>
                    <span className="tabular-nums text-indigo-700">{(dmConfidencePct / 100).toFixed(2)}</span>
                  </legend>
                  <Slider aria-label="DM confidence threshold" value={[dmConfidencePct]} onValueChange={(v) => setDmConfidencePct(v[0])} max={100} step={5} className="mt-2 w-full" disabled={!canEdit} data-testid="safety-dm-confidence" />
                  <div className="text-[10.5px] text-slate-500 mt-1">Maya only replies if she&apos;s ≥ this confident.</div>
                </fieldset>
              </div>

              {/* Working hours */}
              <div>
                <fieldset>
                  <legend className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Working hours (HH:MM)</legend>
                  <div className="mt-1 flex items-center gap-2">
                    <Input id="safety-hours-start" type="time" value={workingStart} onChange={(e) => setWorkingStart(e.target.value)} className="h-8 text-[12px]" disabled={!canEdit} data-testid="safety-hours-start" aria-label="Working hours start" />
                    <span className="text-[11px] text-slate-400">→</span>
                    <Input id="safety-hours-end" type="time" value={workingEnd} onChange={(e) => setWorkingEnd(e.target.value)} className="h-8 text-[12px]" disabled={!canEdit} data-testid="safety-hours-end" aria-label="Working hours end" />
                  </div>
                </fieldset>
              </div>

              {/* Timezone */}
              <div>
                <label htmlFor="safety-timezone" className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">IANA timezone</label>
                <TimezoneSelect
                  id="safety-timezone"
                  testId="safety-timezone"
                  value={timezone}
                  onChange={setTimezone}
                  disabled={!canEdit}
                  triggerClassName="mt-1 h-8 text-[12px]"
                />
              </div>
            </div>

            {/* Redflag terms */}
            <div className="mt-4">
              <label htmlFor="safety-redflag-input" className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Red-flag terms <span className="text-slate-400 normal-case font-normal">(max 50 · 100 chars each — Maya pauses + escalates if she detects any)</span></label>
              <div className="mt-1 flex items-center gap-2">
                <Input id="safety-redflag-input" value={redflagDraft} onChange={(e) => setRedflagDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRedflag(); } }} placeholder='e.g. "lawsuit", "refund"' className="h-8 flex-1 text-[12px]" maxLength={100} disabled={!canEdit} data-testid="safety-redflag-input" />
                <Button size="sm" className="h-8" disabled={!canEdit || !redflagDraft.trim()} onClick={addRedflag} data-testid="safety-redflag-add">Add</Button>
                <span className="text-[10.5px] text-slate-500 tabular-nums">{redflagTerms.length}/50</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {redflagTerms.length === 0 && <span className="text-[11px] text-slate-400 italic">No red-flag terms set.</span>}
                {redflagTerms.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-amber-50 ring-1 ring-amber-200 text-amber-800 px-2 py-0.5 text-[11px]" data-testid={`safety-redflag-chip-${t}`}>
                    {t}
                    {canEdit && <button onClick={() => removeRedflag(redflagTerms.indexOf(t))} className="text-amber-600 hover:text-amber-800" aria-label="Remove">×</button>}
                  </span>
                ))}
              </div>
            </div>

            {/* Escalation emails */}
            <div className="mt-4">
              <label htmlFor="safety-escalation-input" className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Escalation emails <span className="text-slate-400 normal-case font-normal">(max 10)</span></label>
              <div className="mt-1 flex items-center gap-2">
                <Input id="safety-escalation-input" value={escalationDraft} onChange={(e) => setEscalationDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEscalation(); } }} placeholder="founder@yourdomain.com" className="h-8 flex-1 text-[12px]" disabled={!canEdit} data-testid="safety-escalation-input" />
                <Button size="sm" className="h-8" disabled={!canEdit || !escalationDraft.trim()} onClick={addEscalation} data-testid="safety-escalation-add">Add</Button>
                <span className="text-[10.5px] text-slate-500 tabular-nums">{escalationEmails.length}/10</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {escalationEmails.length === 0 && <span className="text-[11px] text-slate-400 italic">No escalation emails. Maya will pause silently on red flags.</span>}
                {escalationEmails.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 ring-1 ring-indigo-200 text-indigo-700 px-2 py-0.5 text-[11px] font-mono" data-testid={`safety-escalation-chip-${e}`}>
                    {e}
                    {canEdit && <button onClick={() => removeEscalation(escalationEmails.indexOf(e))} className="text-indigo-500 hover:text-indigo-700" aria-label="Remove">×</button>}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-500">
                {canEdit ? "Saves to your tenant DB. Maya re-loads on her next conversation turn." : `Admin or owner role required to save (current role: ${role}).`}
              </div>
              <Button
                size="sm"
                className="h-8 text-[11.5px]"
                onClick={onSaveSafety}
                disabled={!canEdit || savingSafety}
                title={!canEdit ? "Admins only" : ""}
                data-testid="safety-save"
              >{savingSafety ? "Saving…" : "Save safety config"}</Button>
            </div>
          </section>
        )}

        {/* Per-channel gauges */}
        <section>
          <div className="text-[15px] font-semibold text-slate-900">Account health</div>
          <p className="text-[12px] text-slate-500 mt-0.5 mb-3">Real-time ban-risk per channel. Lower is safer.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {gauges.map((g) => {
              const connId = channelKeyMap[g.id];
              const labelObj = connId ? getAccountLabel(connId) : { label: "Aman's SMS", connected: true };
              const cooled = coolingDown[g.id];
              const score = cooled ? Math.max(8, g.score - 12) : g.score;
              return (
                <div key={g.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`safety-gauge-${g.id}`}>
                  <div className="flex items-center gap-2">
                    <g.Icon size={14} className="text-slate-500" />
                    <div className="text-[12px] font-semibold text-slate-800 truncate">{labelObj?.label}</div>
                  </div>
                  <div className="mt-2 flex items-center justify-center">
                    <div className="relative h-24 w-24">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadialBarChart innerRadius="68%" outerRadius="100%" data={[{ value: score, fill: ringColor(score) }]} startAngle={90} endAngle={-270}>
                          <RadialBar dataKey="value" cornerRadius={4} background={{ fill: "#F1F5F9" }} />
                        </RadialBarChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-[20px] font-semibold tabular-nums text-slate-900 leading-none">{score}</span>
                        <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">risk</span>
                      </div>
                    </div>
                  </div>
                  <div className={cn("mt-2 inline-flex items-center gap-1 rounded-full ring-1 px-1.5 py-0.5 text-[10px] font-semibold", ringTint(score))}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: ringColor(score) }} />
                    {riskScoreLabel(score)}
                  </div>
                  <div className="mt-2 space-y-1 text-[10.5px] text-slate-600">
                    <div className="flex items-center justify-between"><span>Velocity</span><span className="tabular-nums text-slate-700">{g.velocity}/{g.capDaily}</span></div>
                    <div className="h-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-indigo-400" style={{ width: `${(g.velocity / g.capDaily) * 100}%` }} /></div>
                    <div className="flex items-center justify-between"><span>Complaints</span><span className="tabular-nums text-slate-700">{g.complaint}%</span></div>
                    <div className="flex items-center justify-between"><span>Diversity</span><span className="tabular-nums text-slate-700">{g.diversity}%</span></div>
                  </div>
                  <div className="mt-2 text-[10.5px] text-slate-500 leading-snug">{g.sentence}</div>
                  <Button
                    size="sm" variant="outline" className="w-full mt-2 h-7 text-[10.5px]"
                    onClick={() => { setCoolingDown((m) => ({ ...m, [g.id]: true })); toast(`Maya paused outreach for 2h to cool down ${labelObj?.label}`); }}
                    disabled={cooled}
                    data-testid={`safety-cooldown-${g.id}`}
                  >{cooled ? "Cooling… 2h" : "Cooldown 2h"}</Button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Warmup schedules */}
        <section>
          <div className="text-[15px] font-semibold text-slate-900">Warmup schedules</div>
          <p className="text-[12px] text-slate-500 mt-0.5 mb-3">New accounts ramp gradually so they look human.</p>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr><th className="text-left px-3 py-2">Account</th><th className="text-left px-3 py-2">Current</th><th className="text-left px-3 py-2">Target</th><th className="text-left px-3 py-2">Days in</th><th className="text-left px-3 py-2">Daily ramp</th><th className="text-left px-3 py-2">Full ramp</th></tr>
              </thead>
              <tbody>
                {warmups.map((w) => (
                  <tr key={w.account} className="border-t border-slate-100" data-testid={`warmup-row-${w.account}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{w.account}</td>
                    <td className="px-3 py-2 tabular-nums">{w.current}/day</td>
                    <td className="px-3 py-2 tabular-nums">{w.target}/day</td>
                    <td className="px-3 py-2 tabular-nums">{w.daysIn}</td>
                    <td className="px-3 py-2 text-indigo-700">{w.ramp}</td>
                    <td className="px-3 py-2 text-slate-500">in {w.daysLeft} days</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t border-slate-100 px-3 py-2.5 flex items-center justify-between">
              <div className="text-[11px] text-slate-500">Maya bumps daily volume by 8-12% as the account stays clean.</div>
              <Button size="sm" variant="outline" className="h-7 text-[10.5px]" onClick={() => toast("Pick an account from your pool to warm up")} data-testid="warmup-start">Start warmup</Button>
            </div>
          </div>
        </section>

        {/* TOS Acknowledgments */}
        <section>
          <div className="text-[15px] font-semibold text-slate-900">Platform acknowledgments</div>
          <p className="text-[12px] text-slate-500 mt-0.5 mb-3">Running automation on personal accounts may violate platform terms. Acknowledge that you understand the risk for each channel.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { id: "whatsapp", Icon: MessageCircle, label: "WhatsApp", text: "WhatsApp's Terms prohibit unauthorized automation on personal numbers. By proceeding, you accept the risk." },
              { id: "linkedin", Icon: Linkedin, label: "LinkedIn", text: "LinkedIn's User Agreement restricts automated activity. Maya keeps to human-level pacing — but the risk is yours." },
              { id: "gmail", Icon: Mail, label: "Gmail", text: "Sending automated outreach from a personal Gmail can affect your sender reputation. You're acknowledging that risk." },
            ].map((t) => {
              const accepted = !!tosByChannel?.[t.id];
              return (
                <div key={t.id} className={cn("rounded-xl border p-3", accepted ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30")} data-testid={`tos-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <t.Icon size={13} className={accepted ? "text-emerald-600" : "text-amber-600"} />
                    <div className="text-[12.5px] font-semibold text-slate-900">{t.label}</div>
                    {accepted && <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold"><CheckCircle2 size={10} /> Acknowledged</span>}
                  </div>
                  <div className="mt-1.5 text-[11px] text-slate-600 leading-snug">{t.text}</div>
                  <label className="mt-2 flex items-start gap-2 text-[11px] cursor-pointer">
                    <input type="checkbox" checked={accepted} onChange={(e) => setTosForChannel(t.id, e.target.checked)} className="mt-0.5" data-testid={`tos-check-${t.id}`} />
                    <span>I understand and accept this risk.</span>
                  </label>
                  {accepted && (
                    <div className="mt-1.5 text-[10px] text-slate-500 font-mono">Accepted Feb 14, 2026 · IP 49.36.218.91</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Alert config */}
        <section>
          <div className="text-[15px] font-semibold text-slate-900">Alert me when…</div>
          <p className="text-[12px] text-slate-500 mt-0.5 mb-3">Maya pings you for the things you care about.</p>
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {[
              { key: "banRisk", label: "Ban-risk score crosses 70 on any channel" },
              { key: "pattern", label: "Unusual reply pattern detected (sudden silence or spike)" },
              { key: "disco", label: "A paired account disconnects unexpectedly" },
              { key: "cooldown", label: "Maya automatically triggers a cooldown" },
            ].map((row) => (
              <div key={row.key} className="px-4 py-3 flex items-center gap-3" data-testid={`alert-row-${row.key}`}>
                <Switch checked={alerts[row.key].on} onCheckedChange={(v) => setAlertOn(row.key, v)} data-testid={`alert-toggle-${row.key}`} />
                <div className="flex-1 text-[12px] text-slate-800">{row.label}</div>
                <select
                  value={alerts[row.key].channel}
                  onChange={(e) => setAlertChannel(row.key, e.target.value)}
                  disabled={!alerts[row.key].on}
                  className="text-[11px] border border-slate-200 bg-white rounded-md px-2 py-1 disabled:opacity-40"
                  data-testid={`alert-channel-${row.key}`}
                >
                  {["Email", "WhatsApp DM", "Slack", "All"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Shell>
  );
}

function riskScoreLabel(score) {
  if (score <= 30) return "Healthy";
  if (score <= 70) return "Watch";
  return "Risk";
}

function periodButtonLabel(period) {
  if (period === "week") return "This week";
  if (period === "month") return "This month";
  if (period === "quarter") return "This quarter";
  return "This year";
}

export function SettingsPerformance() {
  const maya = useAssistantName();
  const [period, setPeriod] = useState("month");
  // Paired-account breakdown
  const pairedBreakdown = [
    { account: "Aman's WhatsApp", pct: 38, conv: 1842, channel: "whatsapp" },
    { account: "Aman's LinkedIn", pct: 27, conv: 1306, channel: "linkedin" },
    { account: "Aman's Gmail", pct: 22, conv: 1064, channel: "email" },
    { account: "Aman's Telegram", pct: 8, conv: 384, channel: "telegram" },
    { account: "Aman's SMS", pct: 5, conv: 246, channel: "sms" },
  ];
  // Top 8 campaigns by ROI
  const topCampaigns = [...campaignAnalytics].sort((a, b) => b.roi - a.roi).slice(0, 8);
  // Attribution model
  const [model, setModel] = useState("multi");
  const attrModels = {
    first: { label: "First-touch", key: "first" },
    last: { label: "Last-touch", key: "last" },
    linear: { label: "Linear", key: "linear" },
    decay: { label: "Time-decay", key: "decay" },
    u: { label: "U-shaped", key: "first" },
    multi: { label: "Multi-touch", key: "linear" },
  };
  const attrField = attrModels[model].key;
  const attrTotal = revenueAttribution.reduce((s, r) => s + r[attrField], 0);

  // Funnel — derive 6 stages from existing data
  const funnel = [
    { stage: "Signals", count: funnelData[1].count, color: "from-slate-400 to-slate-500" },
    { stage: "Contacted", count: funnelData[2].count, color: "from-sky-400 to-sky-500" },
    { stage: "Replied", count: funnelData[3].count, color: "from-indigo-400 to-indigo-500" },
    { stage: "Meeting Booked", count: funnelData[4].count, color: "from-violet-400 to-violet-500" },
    { stage: "Deal Created", count: funnelData[5].count, color: "from-amber-400 to-amber-500" },
    { stage: "Closed Won", count: funnelData[7].count, color: "from-emerald-400 to-emerald-500" },
  ];
  const funnelMax = funnel[0].count;
  // Find biggest drop-off
  let maxDropIdx = 1;
  let maxDropPct = 0;
  for (let i = 1; i < funnel.length; i++) {
    const drop = 1 - (funnel[i].count / funnel[i - 1].count);
    if (drop > maxDropPct) { maxDropPct = drop; maxDropIdx = i; }
  }

  // Maya's wins (5)
  const wins = [
    { id: "w1", text: "Closed $48k with Linear after 3 weeks of nurture across WhatsApp + email", when: "2d ago" },
    { id: "w2", text: "Reactivated dormant Stripe contact via signal pickup from r/RevOps thread", when: "5d ago" },
    { id: "w3", text: "Saved 12h on inbound triage Friday alone — 47 WhatsApp threads resolved", when: "1w ago" },
    { id: "w4", text: "Booked the Anthropic demo in 4 messages — first reply to close in 11 min", when: "1w ago" },
    { id: "w5", text: "Reverse-prospected 8 founders who posted about SDR fatigue · 3 demos booked", when: "2w ago" },
  ];

  const channelMeta = { whatsapp: MessageCircle, linkedin: Linkedin, email: Mail, telegram: Send, sms: MessageSquare };

  return (
    <Shell title="Performance" desc={`How ${maya} is performing — and what she saved you.`}>
      <div className="p-6 space-y-8 max-w-6xl">

        {/* Period selector */}
        <div className="flex items-center gap-1.5">
          {["week", "month", "quarter", "year"].map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`} className={cn("text-[11.5px] font-medium px-2.5 py-1 rounded-full transition-colors", period === p ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300")}>
              {periodButtonLabel(p)}
            </button>
          ))}
        </div>

        {/* HERO — Maya's impact */}
        <section className="rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/70 via-white to-white p-7" data-testid="impact-hero">
          <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold mb-2 inline-flex items-center gap-1"><Sparkles size={11} /> {maya}&apos;s impact this {period}</div>
          <div className="flex items-baseline gap-4 flex-wrap">
            <div className="text-[56px] font-bold tracking-tight tabular-nums text-slate-900 leading-none">{aiVsHumanCards.deflected.toLocaleString()}</div>
            <div className="text-[15px] text-slate-600">conversations deflected</div>
            <div className="ml-auto inline-flex items-center gap-1.5 text-emerald-600 text-[12px] font-semibold"><TrendingUp size={12} /> +18% vs last {period}</div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-white ring-1 ring-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">SDR hours saved</div>
              <div className="mt-1 text-[26px] font-bold tabular-nums text-slate-900">{aiVsHumanCards.sdrTimeSaved}</div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">≈ <span className="font-semibold text-slate-700">29 weeks</span> of an SDR&apos;s output</div>
            </div>
            <div className="rounded-xl bg-white ring-1 ring-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pipeline attributed</div>
              <div className="mt-1 text-[26px] font-bold tabular-nums text-slate-900">{aiVsHumanCards.revenueAttributed}</div>
              <div className="text-[10.5px] text-emerald-600 mt-0.5 inline-flex items-center gap-1 font-medium"><TrendingUp size={10} /> +$184k vs last month</div>
            </div>
            <div className="rounded-xl bg-white ring-1 ring-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Human SDRs replaced</div>
              <div className="mt-1 text-[26px] font-bold tabular-nums text-slate-900">{aiVsHumanCards.hoursReplaced}</div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">at $85k/yr each</div>
            </div>
          </div>
          <div className="mt-5 rounded-lg bg-emerald-50 ring-1 ring-emerald-200 px-4 py-2.5 text-[12.5px] text-emerald-800 inline-flex items-center gap-2">
            <Trophy size={13} className="text-emerald-600" /> Estimated value vs hiring 5 SDRs @ $85k/yr each = <span className="font-bold">$425k saved annually</span>
          </div>
        </section>

        {/* PAIRED ACCOUNTS */}
        <section data-testid="paired-breakdown">
          <h2 className="text-[16px] font-semibold tracking-tight text-slate-900 mb-1">Where {maya} spends her time</h2>
          <p className="text-[12px] text-slate-500 mb-3">Conversations by paired account · {pairedBreakdown.reduce((s, p) => s + p.conv, 0).toLocaleString()} total this {period}</p>
          <div className="rounded-xl border border-slate-200 p-5 space-y-3">
            {pairedBreakdown.map(p => {
              const Icon = channelMeta[p.channel] || MessageSquare;
              return (
                <div key={p.account} className="flex items-center gap-3">
                  <div className="flex items-center gap-2 w-44 shrink-0">
                    <Icon size={13} className="text-slate-500" />
                    <span className="text-[12px] font-medium text-slate-700 truncate">{p.account}</span>
                  </div>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500" style={{ width: `${p.pct}%` }} />
                  </div>
                  <div className="w-20 text-right shrink-0">
                    <span className="text-[12.5px] font-semibold tabular-nums text-slate-900">{p.pct}%</span>
                    <span className="text-[10.5px] text-slate-400 ml-1.5">· {p.conv.toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* OUTREACH TABLE */}
        <section data-testid="outreach-perf">
          <h2 className="text-[16px] font-semibold tracking-tight text-slate-900 mb-1">Outreach performance</h2>
          <p className="text-[12px] text-slate-500 mb-3">Top 8 sequences by ROI · {campaignAnalytics.length} total</p>
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Sequence</th>
                  <th className="px-3 py-2 text-right font-semibold">Prospects</th>
                  <th className="px-3 py-2 text-right font-semibold">Reply rate</th>
                  <th className="px-3 py-2 text-right font-semibold">Meetings</th>
                  <th className="px-3 py-2 text-right font-semibold">Opps</th>
                  <th className="px-3 py-2 text-right font-semibold">Pipeline</th>
                  <th className="px-3 py-2 text-right font-semibold">ROI</th>
                </tr>
              </thead>
              <tbody>
                {topCampaigns.map((c, i) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{c.sent.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{c.replyRate.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{c.meetings}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{c.opps}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-900 font-semibold">${(c.pipeline / 1000).toFixed(0)}k</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className={cn("inline-block rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold ring-1", i === 0 ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-slate-50 text-slate-700 ring-slate-200")}>{c.roi.toFixed(1)}×</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ATTRIBUTION */}
        <section data-testid="attribution">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-[16px] font-semibold tracking-tight text-slate-900">Revenue attribution</h2>
              <p className="text-[12px] text-slate-500">${(attrTotal / 1000).toFixed(0)}k total · {attrModels[model].label} model</p>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {Object.entries(attrModels).map(([k, m]) => (
                <button key={k} onClick={() => setModel(k)} data-testid={`attr-${k}`} className={cn("text-[10.5px] font-medium px-2 py-1 rounded-full transition-colors", model === k ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300")}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 p-4">
            <ChartContainer style={{ height: 240 }}>
              <BarChart data={revenueAttribution.map(r => ({ source: r.source, value: r[attrField] }))} margin={{ top: 10, right: 12, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="attrFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366F1" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="source" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: "#94A3B8" }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "rgba(99,102,241,0.05)" }} formatter={(v) => `$${v.toLocaleString()}`} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="value" fill="url(#attrFill)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </div>
        </section>

        {/* FUNNEL */}
        <section data-testid="funnel">
          <h2 className="text-[16px] font-semibold tracking-tight text-slate-900 mb-1">Conversion funnel</h2>
          <p className="text-[12px] text-slate-500 mb-3">From first signal to closed deal</p>
          <div className="rounded-xl border border-slate-200 p-5 space-y-1.5">
            {funnel.map((f, i) => {
              const width = (f.count / funnelMax) * 100;
              const convPct = i > 0 ? ((f.count / funnel[i - 1].count) * 100).toFixed(1) : null;
              return (
                <div key={f.stage}>
                  <div className="flex items-center gap-3">
                    <div className="w-32 text-[12px] text-slate-700 font-medium shrink-0">{f.stage}</div>
                    <div className="flex-1 h-7 rounded-md bg-slate-50 overflow-hidden relative">
                      <div className={cn("h-full bg-gradient-to-r rounded-md flex items-center px-2 text-white text-[11px] font-semibold", f.color)} style={{ width: `${width}%`, minWidth: 60 }}>
                        {f.count.toLocaleString()}
                      </div>
                    </div>
                    {convPct && <div className="w-16 text-right text-[11px] text-slate-500 shrink-0 tabular-nums">{convPct}% conv.</div>}
                  </div>
                  {i < funnel.length - 1 && <div className="ml-32 my-0.5 text-slate-300"><ArrowDown size={9} /></div>}
                </div>
              );
            })}
          </div>
          <div className="mt-3 rounded-lg bg-indigo-50 ring-1 ring-indigo-200 p-3 text-[12px] text-slate-700">
            <Sparkles size={11} className="inline mr-1 text-indigo-600" />
            <span className="italic">Biggest leak: {(maxDropPct * 100).toFixed(0)}% drop between <span className="font-semibold">{funnel[maxDropIdx - 1].stage}</span> → <span className="font-semibold">{funnel[maxDropIdx].stage}</span>. {maya} suggests a post-meeting follow-up sequence to close this gap.</span>
          </div>
        </section>

        {/* MAYA'S WINS */}
        <section data-testid="wins">
          <h2 className="text-[16px] font-semibold tracking-tight text-slate-900 mb-1 inline-flex items-center gap-1.5"><Trophy size={14} className="text-amber-500" /> {maya}&apos;s wins this {period}</h2>
          <p className="text-[12px] text-slate-500 mb-3">Specific outcomes — not vanity metrics.</p>
          <div className="space-y-1.5">
            {wins.map(w => (
              <div key={w.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span className="text-[12.5px] text-slate-800 flex-1">{w.text}</span>
                <span className="text-[10.5px] text-slate-400 shrink-0">{w.when}</span>
              </div>
            ))}
          </div>
        </section>

      </div>
    </Shell>
  );
}

export function SettingsPrivacy() {
  return (
    <Shell title="Privacy & Data Sovereignty" desc="Your data never leaves your instance.">
      <div className="p-8 max-w-2xl">
        <p className="text-[14px] text-slate-700 leading-relaxed">Every conversation, every contact, every voice memo — stored only in your instance. Encrypted at rest with keys you control. We can&apos;t see it. Your AI provider can&apos;t see it. Your competitors can&apos;t subpoena it.</p>
        <div className="mt-5 flex flex-wrap gap-2">{["SOC 2 Type II", "GDPR compliant", "AES-256 at rest", "TLS 1.3 in transit", "Bring your own KMS"].map(t => <span key={t} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-2.5 py-1 text-[11px] font-medium"><ShieldCheck size={11} /> {t}</span>)}</div>
      </div>
    </Shell>
  );
}

export function SettingsAudit() {
  const entries = teamActivity || [];
  return (
    <Shell title="Audit log" desc="Every action across your team and Maya.">
      <div className="p-6 max-w-4xl">
        {entries.length ? (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {entries.map((a) => (
              <div key={a.id} className="px-4 py-2 flex items-center gap-3 text-[12.5px]">
                <Avatar member={a.member} size={22} />
                <div className="flex-1 text-slate-700">
                  <span className="font-semibold text-slate-900">{a.member.first}</span> {a.action}
                </div>
                <span className="text-[10.5px] text-slate-400">{a.at}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-14 text-center" data-testid="audit-empty">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100">
              <Activity size={20} strokeWidth={1.75} />
            </div>
            <p className="mt-4 text-[15px] font-semibold text-slate-900">No audit events yet</p>
            <p className="mt-1.5 text-[13px] text-slate-600 max-w-md mx-auto">
              Team changes and security actions are recorded automatically. Activity will appear here once events are logged.
            </p>
          </div>
        )}
      </div>
    </Shell>
  );
}

// Redirect old paths
export function ToToday() { return <Navigate to="/today" replace />; }
export function ToInbox({ keepPath = "" }) { const p = window.location.pathname.replace(/^\/inbound/, "/inbox") + window.location.search; return <Navigate to={p || "/inbox"} replace />; }
ToInbox.propTypes = { keepPath: PropTypes.string };
export function ToPeople() { return <Navigate to="/people" replace />; }
export function ToDeals() { return <Navigate to="/people/deals" replace />; }
export function ToOutreach() { const p = window.location.pathname.replace(/^\/outbound/, "/outreach") + window.location.search; return <Navigate to={p || "/outreach"} replace />; }
export function ToBrain() { const p = window.location.pathname.replace(/^\/knowledge/, "/brain") + window.location.search; return <Navigate to={p || "/brain"} replace />; }
